<template>
  <div class="conversations-page" :class="{ 'wa-composer-blocked': composerBlocked }">
    <!-- Page Header -->
    <div class="conversations-page-header">
      <div class="header-icon-wrapper">
        <i class="fa fa-comments"></i>
      </div>
      <div class="header-titles">
        <h1>Patient Conversations</h1>
        <!-- WhatsApp-only office (SMS_OUTBOUND_DISABLED): don't advertise SMS or the outgoing SMS number. -->
        <p v-if="smsOutboundOff" data-test="conv-header-whatsapp-only">WhatsApp messaging with your patients — texting is turned off for this office</p>
        <p v-else>SMS messaging with your patients</p>
        <span v-if="smsPhoneNumber && !smsOutboundOff" class="sms-number-pill">
          <i class="fa fa-phone"></i> {{ siteName }}'s outgoing SMS number: <strong>{{ smsPhoneNumber }}</strong>
        </span>
        <!-- Photo-display note. The chat shows at most MAX_RENDERED_MEDIA_PER_MESSAGE photos per
             text (vue-advanced-chat renders one file per bubble, so a multi-photo text is fanned
             out). Staff must know the cap exists, or a 3-photo text looks like we lost one.
             The Open Dental half is CONDITIONAL: photos only reach imaging when the site has an
             imaging category configured (Conversations settings). Batavia has none in prod, so an
             unconditional "they all go to Open Dental" would be a false promise there. -->
        <span class="photo-note">
          <i class="fa fa-camera"></i> {{ photoDisplayNote }}
        </span>
      </div>
      <button class="new-conversation-btn" @click="addRoom" :disabled="!settingsReady">
        <i class="fa fa-plus"></i>
        <span>New Conversation</span>
      </button>
      <button class="conv-settings-btn" @click="openConversationsSettings" title="SMS Conversation Settings">
        <i class="fa fa-cog"></i>
      </button>
    </div>
    <!-- Mount point for vue2-slideout-panel (ConversationsSettings renders here). Required by the plugin. -->
    <slideout-panel></slideout-panel>

    <!-- New Conversation: ONE of three views (newConversationView). A WhatsApp-only office with no approved
         message cannot open a conversation at all, so it gets the explainer card instead of a form whose Start
         button could never be useful; an office with an approved message (or one that can text) gets the form. -->
    <div v-if="settingsReady && addNewRoom && newConversationView === 'form'" class="chat-forms">
      <form @submit.prevent="createRoom">
        <h3>Start New Conversation</h3>
        <!-- WhatsApp-only office that already has an approved message: one visible line, not small grey print at the
             bottom. Its own element (not inside a .form-row, which lays children out side by side). -->
        <p v-if="smsOutboundOff" class="wa-form-callout" data-test="new-conv-whatsapp-only-note">
          <i class="fa fa-whatsapp"></i>
          <span>{{ formCallout }}</span>
        </p>
        <!-- Label + control, not a long placeholder. A placeholder is the only thing naming these
             controls, and it is the first thing to be truncated: at 390px the phone box had 208px
             of room for a 240px placeholder, so it read "Text a number not in Open De…" (measured
             2026-09-18). A label cannot truncate — it wraps — and it stays readable once the field
             has been typed into, which a placeholder does not. The placeholder now only shows the
             expected FORMAT. -->
        <div class="form-row">
          <div class="field">
            <span class="field-label">Existing patient</span>
            <multiselect
              v-if="patientsToChatWith"
              v-model="selectedUsers"
              :options="patientsToChatWith"
              :multiple="false"
              :taggable="false"
              :show-labels="false"
              label="Patient"
              :custom-label="patientCustomLabel"
              track-by="PatNum"
              placeholder="Search by name"
              @input="onPatientSelected"
            />
          </div>
        </div>

        <!-- TEXT SOMEONE WHO IS NOT A PATIENT -->
        <!-- The two controls are mutually exclusive by design: each one clears the other, and
             resolveNewConversationTarget() REFUSES a submit that somehow has both, rather than
             picking one. Two recipients on screen and one text going out is how the wrong person
             gets a message with nobody noticing.
             Deliberately a plain <input>, NOT :taggable on the multiselect above: vue-multiselect
             commits typed text only on Enter and wipes `search` on blur, so a number typed and not
             Entered would look submitted and silently do nothing. -->
        <!-- The divider and the consent note are wrapped in .form-row on purpose. An UNSCOPED
             style section in AppointmentCalendar.vue leaks a max-width 768px media rule for
             .form-row to every page in the app, setting flex-direction to column; this component
             adds align-items center on top. So at tablet and phone widths the controls stack and
             centre, and anything NOT inside a .form-row stays left-aligned and drifts ~116px from
             the inputs it belongs to (measured at 768px, 2026-09-18). Following the rule that is
             already there beats adding a competing media query that has to be kept in sync. -->
        <div class="form-row">
          <div class="or-divider"><span>or</span></div>
        </div>
        <div class="form-row">
          <div class="field">
            <span class="field-label">Number not in Open Dental</span>
            <input
              type="tel"
              inputmode="tel"
              autocomplete="off"
              maxlength="25"
              class="search-input new-number-input"
              v-model="typedPhone"
              @input="onTypedPhoneInput"
              placeholder="(555) 123-4567"
            />
          </div>
        </div>
        <div class="form-row">
          <p class="consent-note">
            <i class="fa fa-info-circle"></i>
            Only text people who have asked the practice to contact them.
          </p>
        </div>

        <!-- The two buttons live in one .form-actions box that is the SAME width as the controls
             above (400px, capped to 100%), with each button flex: 1. Before this they sized to
             their own text and centred independently — at 390px that painted a 150px Start at
             x=136 and an 82px Cancel at x=170, against controls 238px wide at x=92: three
             different widths and three different left edges in one card. -->
        <div class="form-row">
          <div class="form-actions">
            <button type="submit" :disabled="disableForm || creatingConversation || !canStartConversation">
              <span v-if="creatingConversation"><i class="fa fa-spinner fa-spin"></i> Creating...</span>
              <span v-else>Start Conversation</span>
            </button>
            <button type="button" class="button-cancel" @click="addNewRoom = false; resetForms();">Cancel</button>
          </div>
        </div>
      </form>
    </div>
    <!-- While we find out whether the office has an approved message: neither view (no flash of the wrong one). -->
    <div v-else-if="settingsReady && addNewRoom && newConversationView === 'loading'" class="wa-start-card wa-start-card--loading" data-test="new-conv-wa-loading" role="status" aria-live="polite">
      <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
      <span>One moment&hellip;</span>
    </div>
    <div v-else-if="settingsReady && addNewRoom && newConversationView === 'explainer'" class="wa-start-card" data-test="new-conv-wa-explainer" role="region" aria-labelledby="wa-start-card-title">
      <div class="wa-start-card__head">
        <span class="wa-start-card__icon" aria-hidden="true"><i class="fa fa-whatsapp"></i></span>
        <div class="wa-start-card__intro">
          <h3 id="wa-start-card-title" class="wa-start-card__title">{{ explainerCopy.title }}</h3>
          <p class="wa-start-card__text">{{ explainerCopy.intro }}</p>
        </div>
      </div>
      <p class="wa-start-card__note" data-test="new-conv-wa-automatic-note"><strong>{{ explainerCopy.automatic.lead }}</strong> {{ explainerCopy.automatic.body }}</p>
      <ol class="wa-start-card__steps">
        <li v-for="(step, i) in explainerCopy.steps" :key="i" class="wa-start-card__step">
          <span class="wa-start-card__num" aria-hidden="true">{{ i + 1 }}</span>
          <span class="wa-start-card__step-text">
            <strong>{{ step.title }}</strong>
            <span>{{ step.body }}</span>
          </span>
        </li>
      </ol>
      <div class="wa-start-card__actions">
        <router-link v-if="canAddWhatsAppMessages" :to="whatsappTabLocation" class="wa-start-card__cta" data-test="new-conv-wa-explainer-cta">
          {{ explainerCopy.cta }} <i class="fa fa-arrow-right" aria-hidden="true"></i>
        </router-link>
        <p v-else class="wa-start-card__ask" data-test="new-conv-wa-explainer-ask">{{ explainerCopy.askAdmin }}</p>
        <button type="button" class="wa-start-card__close" data-test="new-conv-wa-explainer-close" @click="addNewRoom = false; resetForms();">{{ explainerCopy.close }}</button>
      </div>
      <p class="wa-start-card__fine">{{ explainerCopy.finePrint }}</p>
    </div>
    <!-- END new-conv-wa-explainer -->

    <!-- GROUP MMS WARNING BANNER -->
    <!-- This appears when the selected conversation is a Group MMS (patient texted multiple recipients) -->
    <div v-if="isCurrentRoomGroupMMS" class="group-mms-warning">
      <i class="fa fa-exclamation-triangle"></i>
      <div class="warning-content">
        <strong>Group Conversation</strong>
        <p>This patient texted multiple recipients. Your replies will be visible to all participants in the group.</p>
      </div>
    </div>

    <!-- "NOT IN OPEN DENTAL" BADGE — thread header half (the room-row pill is the other half,
         injected by applyNotInOpenDentalRoomStyles). Shows when the thread's phone never resolved
         to a patient, which is exactly when no commlog row is written for it. Also lights up the
         pre-existing inbound-from-a-stranger threads, which have the same consequence. -->
    <div v-if="currentRoomNotInOpenDental" class="not-in-od-banner">
      <i class="fa fa-info-circle"></i>
      <span>Not in Open Dental — messages won't appear in the patient's chart.</span>
    </div>

    <!-- REPLY-CHANNEL SELECTOR (only when the site offers WhatsApp) -->
    <!-- The agent explicitly chooses SMS vs WhatsApp; the default follows the patient's last
         inbound channel. WhatsApp shows its 24h window status and free-form is blocked once the
         window closes (the send handler enforces this too, so a stale UI can't send blind). -->
    <!-- Hidden at a WhatsApp-only office while the window is closed: there is no channel to choose, and the
         panel in the message box already says the window has closed (2026-10-14: fewer stacked blocks). -->
    <div v-if="siteWhatsAppEnabled && selectedRoomID && !(smsOutboundOff && composerBlocked)" class="reply-channel-bar">
      <span class="rcb-label">Reply via</span>
      <div class="rcb-toggle">
        <button v-if="!smsOutboundOff" type="button" class="rcb-btn" :class="{ active: effectiveReplyChannel === 'sms' }" @click="setReplyChannel('sms')">
          <i class="fa fa-commenting-o"></i> SMS
        </button>
        <button type="button" class="rcb-btn rcb-btn--wa" :class="{ active: effectiveReplyChannel === 'whatsapp' }" @click="setReplyChannel('whatsapp')">
          <i class="fa fa-whatsapp"></i> WhatsApp
        </button>
      </div>
      <span v-if="effectiveReplyChannel === 'whatsapp'" class="rcb-window" :class="{ closed: !whatsappWindowOpen }">
        <i class="fa" :class="whatsappWindowOpen ? 'fa-check-circle' : 'fa-exclamation-triangle'"></i>
        {{ whatsappWindowOpen ? '24-hour reply window open' : windowClosedHint }}
      </span>
    </div>

    <!-- (The closed-window notice and the approved-message picker are ONE panel, .wa-closed-panel, rendered just
         below the chat and placed inside its message-box area: see the comment there.) -->

    <!-- Chat Window -->
    <!-- NOTE: Using stable key to prevent unnecessary component recreation when rooms re-sort -->
    <!-- Previously: key included rooms.length and rooms[0].roomId which caused flickering -->
    <ConversationsSkeleton v-if="loadingRooms && !rooms.length" />
    <vue-advanced-chat v-show="!loadingRooms || rooms.length > 0"
      :key="'conversations-chat'"
      :current-user-id="effectiveCurrentUserId"
      :room-id="selectedRoomID || ''"
      :rooms="visibleRooms"
      :height="chatHeight"
      :loading-rooms="loadingRooms"
      :rooms-loaded="roomsLoaded"
      :messages="windowedMessages"
      :messages-loaded="messagesLoaded"
      :styles="styles"
      :show-audio="false"
      :show-emojis="false"
      :media-preview-enabled="true"
      :show-add-room="false"
      :load-first-room="false"
      :room-message="prefillRoomMessage"
      :room-actions="roomActions"
      :text-messages="chatTextMessages"
      @add-room="addRoom"
      @room-selected="onRoomSelected"
      @room-action-handler="onRoomActionHandler"
      @fetch-messages="onFetchMessages"
      @send-message="onSendMessage"
      @textarea-action-handler="onTextareaAction"
      @open-file="handleOpenFile"
      v-on="chatListeners"
      >
      <!-- Filter chips above search (All / Unread) -->
      <template v-slot:rooms-header>
        <UnreadFilterChips
          v-model="activeFilter"
          :unread-count="unreadRoomCount"
        />
      </template>
      <!-- "All caught up!" empty state when filter is on but zero unread -->
      <template v-slot:rooms-empty>
        <AllCaughtUpEmptyState
          v-if="activeFilter === 'unread' && rooms && rooms.length > 0"
          @show-all="activeFilter = 'all'"
        />
      </template>
      <!-- Custom icon for textarea action (SMS templates) -->
      <template v-slot:custom-action-icon>
        <i class="fa fa-file-text-o" title="SMS Templates"></i>
      </template>
      <!-- H3 (2026-07-19): the per-message custom slot below was DEAD — vue-advanced-chat 0.6.9 has no
           `message_<id>` slot (only a single full-replacement `message` slot), so none of it ever
           rendered. Unservable legacy media now shows a graceful placeholder at the DATA layer
           (store/conversationsV2 → legacyMediaPlaceholderContent); real media renders via the library
           default. Removed with it: a leftover console.log+alert() debug landmine on file click. -->
    </vue-advanced-chat>

    <!-- CLOSED 24-HOUR WINDOW + APPROVED MESSAGES (Saumil, 2026-10-14): while the window is closed the text box is
         replaced, in place, by this one panel: a plain statement that the window has closed, and, when the office
         has approved messages, a menu (only when there are several), the exact text the patient will receive, and ONE
         Send button (the preview is the confirmation). The browser sends only the message's key; the server fills in
         the practice's details and refuses anything that is not an approved message of this site. The text never
         mentions SMS: an office may not have it.
         vue-advanced-chat exposes no footer slot, so placeClosedPanel() relocates this node into .vac-room-footer,
         the same move-once pattern as the SMS guidance bar below. v-if is kept STABLE (selectedRoomID) and
         visibility is v-show, because toggling a JS-moved node's v-if corrupts Vue's DOM patching. While the window
         is closed the footer's other children are hidden by CSS (nothing is destroyed). -->
    <div v-if="siteWhatsAppEnabled && selectedRoomID" v-show="composerBlocked" class="wa-closed-panel" role="status" data-test="wa-window-banner">
      <div class="wcp-status">
        <span class="wcp-dot" aria-hidden="true"></span>
        <p class="wcp-text"><strong class="wa-wb-title">{{ windowClosedNotice.title }}</strong> <span class="wa-wb-body">{{ windowClosedNotice.body }}</span></p>
      </div>
      <div v-if="chosenApproved" class="wcp-compose" data-test="wa-approved">
        <div v-if="approvedMessages.length > 1" class="wcp-choose">
          <label class="wcp-label" for="wa-approved-select">{{ approvedText.choose }}</label>
          <select id="wa-approved-select" v-model="approvedChoiceKey" class="wcp-select" data-test="wa-approved-select" :disabled="approvedSending">
            <option v-for="m in approvedMessages" :key="m.key" :value="m.key">{{ m.title }}</option>
          </select>
        </div>
        <div class="wcp-preview" data-test="wa-approved-preview">
          <p v-if="approvedMessages.length === 1" class="wcp-preview-title">{{ chosenApproved.title }}</p>
          <p class="wcp-preview-text">{{ chosenApproved.preview }}</p>
          <p class="wcp-preview-caption">{{ approvedText.caption }}</p>
        </div>
        <div class="wcp-actions">
          <button type="button" class="wcp-send" data-test="wa-approved-send" :disabled="approvedSending" @click="sendApproved(chosenApproved)">
            <span v-if="approvedSending" class="wcp-spinner" aria-hidden="true"></span> {{ approvedText.send }}
          </button>
        </div>
      </div>
      <!-- No approved message yet: say what to do next, right here (the same button as the New Conversation card). The
           block sits inside the moved panel, so its v-if toggling is safe (only the panel node itself is JS-moved). -->
      <div v-if="noApprovedNotice" class="wcp-none" data-test="wa-approved-none">
        <div class="wcp-none-body">
          <p class="wcp-none-text">{{ explainerCopy.panelNone }}</p>
          <p class="wcp-none-note" data-test="wa-approved-none-note"><strong>{{ explainerCopy.automatic.lead }}</strong> {{ explainerCopy.automatic.body }}</p>
        </div>
        <!-- The arrow is drawn, not an icon font: the chat library's `.vac-card-window * { font-family: inherit }` ties with
             Font Awesome's `.fa` rule, so inside the chat area the font glyph shows as an empty box on the production build. -->
        <router-link v-if="canAddWhatsAppMessages" :to="whatsappTabLocation" class="wcp-none-cta" data-test="wa-approved-none-cta">
          {{ explainerCopy.cta }}
          <svg class="wcp-arrow" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path d="M2 8h10M8.5 3.5 13 8l-4.5 4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </router-link>
        <p v-else class="wcp-none-ask" data-test="wa-approved-none-ask">{{ explainerCopy.askAdmin }}</p>
      </div>
    </div>

    <!-- Live SMS guidance for the reply composer. vue-advanced-chat owns the composer and exposes no
         footer slot, so Vue renders this here and placeGuidanceBar() relocates it under the composer
         box (inside .vac-room-footer) — parity with the patient SMS bar. v-if is kept STABLE
         (selectedRoomID, unchanged while typing) so this relocated node is created once and never torn
         down mid-session: toggling a JS-moved node's v-if corrupts Vue's DOM patching (guidance
         vanished after clear+retype). Visibility is a reactive CLASS instead — an in-place patch that
         never moves/recreates the node. Same move-once pattern as the "+ Insert" wrapper below. SMS
         replies only (WhatsApp has its own 24h-window guidance + encoding/pricing). -->
    <div v-if="selectedRoomID" class="conv-sms-guidance-bar" :class="{ 'is-visible': showComposerGuidance }">
      <SmsGuidance v-if="effectiveReplyChannel === 'sms'" :text="composerText" />
    </div>

    <!-- + Insert Button (rendered here, then moved into the Quick Actions bar by renderRecentBar) -->
    <div class="insert-button-wrapper" v-if="selectedRoomID">
      <b-dropdown variant="outline-secondary" size="sm" no-caret dropup class="insert-dropdown">
        <template slot="button-content">
          <strong>+ Insert</strong> <i class="fa fa-caret-up ml-1"></i>
        </template>

        <b-dropdown-header>Variables</b-dropdown-header>
        <b-dropdown-item @click="insertVariable('patient_first_name')"><i class="fa fa-user mr-2 text-muted"></i>Patient First Name</b-dropdown-item>
        <b-dropdown-item @click="insertVariable('patient_last_name')"><i class="fa fa-user mr-2 text-muted"></i>Patient Last Name</b-dropdown-item>
        <b-dropdown-item @click="insertVariable('apt_date')"><i class="fa fa-calendar mr-2 text-muted"></i>Appointment Date</b-dropdown-item>
        <!-- 'employee_name' is deliberately not offered: it still resolves (permanent legacy
             alias) but two menu entries meaning the same thing is the confusion this split was
             meant to remove. -->
        <b-dropdown-item @click="insertVariable('employee_full_name')"><i class="fa fa-id-badge mr-2 text-muted"></i>Employee Full Name</b-dropdown-item>
        <b-dropdown-item @click="insertVariable('employee_first_name')"><i class="fa fa-id-badge mr-2 text-muted"></i>Employee First Name</b-dropdown-item>
        <b-dropdown-item @click="insertVariable('recall_due_date')"><i class="fa fa-clock-o mr-2 text-muted"></i>Recall Due Date</b-dropdown-item>
        <b-dropdown-item @click="insertVariable('guarantor_first_name')"><i class="fa fa-user-o mr-2 text-muted"></i>Guarantor First Name</b-dropdown-item>
        <b-dropdown-item @click="insertVariable('guarantor_last_name')"><i class="fa fa-user-o mr-2 text-muted"></i>Guarantor Last Name</b-dropdown-item>
        <b-dropdown-item @click="insertVariable('site_name')"><i class="fa fa-building mr-2 text-muted"></i>Site Name</b-dropdown-item>
        <b-dropdown-item @click="insertVariable('site_address1')"><i class="fa fa-map-marker mr-2 text-muted"></i>Site Address</b-dropdown-item>
        <b-dropdown-item @click="insertVariable('site_city')"><i class="fa fa-map-marker mr-2 text-muted"></i>Site City</b-dropdown-item>
        <b-dropdown-item @click="insertVariable('site_state')"><i class="fa fa-map-marker mr-2 text-muted"></i>Site State</b-dropdown-item>
        <b-dropdown-item @click="insertVariable('site_zip')"><i class="fa fa-map-marker mr-2 text-muted"></i>Site Zip</b-dropdown-item>
        <b-dropdown-item @click="insertVariable('site_officephone')"><i class="fa fa-phone mr-2 text-muted"></i>Office Phone</b-dropdown-item>

        <b-dropdown-divider></b-dropdown-divider>

        <b-dropdown-header>Emojis</b-dropdown-header>
        <div class="emoji-inline-grid px-2 pb-2">
          <div v-for="(emojiGroup, category) in emojiTable" :key="category">
            <h6 class="emoji-category-title">{{ category }}</h6>
            <div class="emoji-grid">
              <span
                v-for="(emoji, emojiName) in emojiGroup"
                :key="emojiName"
                class="emoji-item"
                :title="emojiName"
                @click="insertEmoji(emoji)"
              >{{ emoji }}</span>
            </div>
          </div>
        </div>
      </b-dropdown>
    </div>

    <!-- SMS Template Selection Modal -->
    <SmsTemplateSelectionModal
      v-if="showTemplateModal"
      @send="onTemplateSelected"
      @cancel="showTemplateModal = false"
    />

    <!-- Image Modal/Lightbox -->
    <div v-if="expandedImage" class="image-modal-overlay" @click="closeImageModal">
      <div class="image-modal-content" @click.stop>
        <button class="image-modal-close" @click="closeImageModal" title="Close (Esc)">
          <i class="fa fa-times"></i>
        </button>
        <img 
          :src="expandedImage.src" 
          :alt="expandedImage.alt"
          class="image-modal-image"
          @load="onModalImageLoad"
        />
        <div v-if="expandedImage.filename" class="image-modal-filename">{{ expandedImage.filename }}</div>
      </div>
    </div>
    
  </div>
  
</template>

<script>
/* eslint-disable */
import { mapState, mapActions } from 'vuex'

import { isSmsEnabled, hasSmsNumber } from '@/utils/siteCapability';import ChatWindow from 'vue-advanced-chat'
import 'vue-advanced-chat/dist/vue-advanced-chat.css'
import Multiselect from 'vue-multiselect'
import mixin from '../../mixin.js'
import axios from 'axios'
import SmsTemplateSelectionModal from '../PatientProfile/components/sms/modals/SmsTemplateSelectionModal.vue'
import ConversationsSkeleton from './ConversationsSkeleton.vue'
import ConversationsSettings from './ConversationsSettings.vue'
import UnreadFilterChips from '../../components/UnreadFilterChips.vue'
import AllCaughtUpEmptyState from '../../components/AllCaughtUpEmptyState.vue'
import { isWhatsAppComposerBlocked, isWithinWhatsAppWindow, lastInboundChannel, lastWhatsAppInboundMs, resolveReplyChannel, whatsappWindowClosedHint, whatsappWindowClosedNotice, whatsappWindowClosedToast } from '../../utils/whatsappWindow'
import { fetchApprovedWhatsAppMessages } from '../../services/whatsappMessages'
import {
  APPROVED_CHOOSE_LABEL, APPROVED_PREVIEW_CAPTION, APPROVED_SEND_LABEL, APPROVED_SENT_TOAST,
  buildApprovedSendPayload, approvedSendFailureMessage, resolveChosenApproved,
} from '../../utils/whatsappApprovedMessages'
import {
  resolveNewConversationView, canAddWhatsAppMessages, WHATSAPP_TAB_LOCATION,
  EXPLAINER_TITLE, EXPLAINER_INTRO, EXPLAINER_STEPS, EXPLAINER_CTA, EXPLAINER_ASK_ADMIN, EXPLAINER_FINE_PRINT, EXPLAINER_CLOSE, FORM_CALLOUT,
  PANEL_NONE_TEXT, AUTOMATIC_NOTE, showNoApprovedNotice,
} from '../../utils/whatsappNewConversation'
import { classifyNonDelivery } from '../../utils/messaging'
import { getTextBlockReason, optOutResultFromSendFailure } from '../../utils/textConsent'
import { resolveNewConversationTarget, newConversationUsersPayload, threadIsNotInOpenDental } from '../../utils/patientPhone'
import { demoRoomActions, demoErrorMessage, SIMULATE_REPLY_ACTION } from '../../utils/demo/demoShell'
import { formatPhoneForDisplay } from '../Forms/libraries/format-field-value'
import { getSmsGuidance } from '../../utils/smsGuidance'
import { chatFitHeight } from '../../utils/chatFitHeight'
import { optimisticDrawCredits } from '../../utils/walletCredits'
import { realFileUrl, isLegacyExpiredMedia as legacyExpiredMedia, MAX_RENDERED_MEDIA_PER_MESSAGE } from '../../utils/legacyMedia'
import SmsGuidance from '../../components/SmsGuidance.vue'
import { readRoomIdQuery } from '../../utils/conversationDeepLink'

const VARIABLE_LABELS = {
  patient_first_name: 'Patient First',
  patient_last_name: 'Patient Last',
  apt_date: 'Apt Date',
  // 'employee_name' MUST stay in this map even though it is no longer in the + Insert menu:
  // recentVariables is persisted in localStorage, so a user who inserted it before this change
  // still has it in their "recently used" bar and the chip would render undefined without a label.
  employee_name: 'Employee',
  employee_full_name: 'Employee Full',
  employee_first_name: 'Employee First',
  recall_due_date: 'Recall Date',
  guarantor_first_name: 'Guarantor First',
  guarantor_last_name: 'Guarantor Last',
  site_name: 'Site Name',
  site_address1: 'Address',
  site_city: 'City',
  site_state: 'State',
  site_zip: 'Zip',
  site_officephone: 'Phone',
};

const EMOJI_TABLE = {
    'Frequently Used': { thumbs_up: '👍', heart: '❤️', smile: '😄', wave: '👋', pray: '🙏', party: '🎉', star: '⭐', check: '✅', fire: '🔥', clap: '👏' },
    'Smileys': { grinning: '😀', smiley: '😃', smile: '😄', grin: '😁', laughing: '😆', blush: '😊', wink: '😉', heart_eyes: '😍', kissing_heart: '😘', thinking: '🤔', neutral: '😐', relieved: '😌', sunglasses: '😎', cry: '😢', sob: '😭', joy: '😂' },
    'Gestures': { thumbs_up: '👍', thumbs_down: '👎', ok_hand: '👌', wave: '👋', clap: '👏', raised_hands: '🙌', pray: '🙏', muscle: '💪', point_right: '👉', point_left: '👈' },
    'Celebrations': { party: '🎉', confetti: '🎊', balloon: '🎈', gift: '🎁', cake: '🎂', trophy: '🏆', sparkles: '✨', star: '⭐', tada: '🎊', ribbon: '🎀' },
    'Hearts & Symbols': { red_heart: '❤️', orange_heart: '🧡', yellow_heart: '💛', green_heart: '💚', blue_heart: '💙', purple_heart: '💜', check: '✅', x: '❌', exclamation: '❗', question: '❓' }
};

export default {
  name: 'ConversationContainerV2',
  components: { ChatWindow, Multiselect, 'vue-advanced-chat': ChatWindow, SmsTemplateSelectionModal, ConversationsSkeleton, UnreadFilterChips, AllCaughtUpEmptyState, SmsGuidance },
  props: { currentUserId: String },
  mixins: [mixin],
  data() {
    return {
      // ?roomId= already opened on this page instance (the deep link opens its thread once).
      deepLinkRoomHandled: null,
      addNewRoom: false,
      disableForm: false,
      creatingConversation: false,
      selectedUsers: [],
      // Raw text of the "text a number not in Open Dental" box. Mutually exclusive with
      // selectedUsers; both are read by resolveNewConversationTarget() in createRoom().
      typedPhone: '',
      initialized: false,
      selectedRoomID: '',
      // Agent's explicit reply-channel choice for the open thread ('sms'|'whatsapp'); null = use
      // the smart default (patient's last-inbound channel). Reset when switching rooms.
      replyChannelOverride: null,
      // The office's approved messages staff can send while the 24-hour window is closed (step 4 part 2). Loaded
      // once per site, the first time a closed window is on screen; a failed load just leaves the list empty.
      approvedMessages: [],
      approvedLoadedFor: '',
      // 'idle' -> 'loading' -> 'loaded' | 'failed'. newConversationView needs to tell "none yet" from "not asked yet".
      approvedStatus: 'idle',
      approvedPick: '',
      approvedSending: false,
      // "Now", refreshed every minute, so the 24-hour window can close on screen (see whatsappWindowOpen).
      nowMs: Date.now(),
      nowTimer: null,
      composerText: '', // live text mirrored from the vue-advanced-chat textarea → drives <SmsGuidance>
      // Chat window height, measured from where the chat actually starts (fitChatToViewport) so the
      // composer is on screen without scrolling. A fixed 'calc(100vh - 220px)' pushed it below the
      // fold: the header, photo note and the conditional banners/Reply-via bar stack up well past 220px.
      chatHeight: 'calc(100vh - 220px)',
      styles: {
        container: {
          marginTop: '10px',
          borderRadius: '4px',
          height: 'calc(100vh - 120px)',
          minHeight: '420px'
        }
      },
      suppressAutoFetchUntil: 0,
      debugImageUrl: '',
      imageLoadingStates: {}, // Track loading state for each image by message ID
      expandedImage: null, // Track currently expanded image for modal
      showTemplateModal: false, // SMS template selection modal
      prefillRoomMessage: null, // Prefill textarea with template content
      templateLinkMap: {}, // Map of [Form: X] placeholders → <a> HTML anchors for restoration
      templateWikiPageID: null, // WikiPageID of selected template
      // autoSelectedRoomId removed — load-first-room=false prevents auto-select entirely
      // Recently used bar
      recentEmojis: JSON.parse(localStorage.getItem('teamio-sms-recent-emojis') || 'null') || ['👍', '😄', '❤️', '✅', '🙏', '🎉'],
      recentVariables: JSON.parse(localStorage.getItem('teamio-sms-recent-variables') || 'null') || [],
      recentTemplates: JSON.parse(localStorage.getItem('teamio-sms-recent-templates') || 'null') || [],
      recentBarEl: null,
      activeFilter: 'all',
      // Cap on messages rendered into vue-advanced-chat — same pattern as
      // ChatContainer.vue. The library re-renders every Message child on
      // every keystroke (scoped-slot fan-out defeats child-render skip),
      // so per-keystroke cost scales linearly with rendered count
      // (~0.2ms / rendered msg, profiled on prod). Cap = 25 keeps the
      // per-keystroke render cost within ~5ms of the empty-room baseline
      // so busy threads feel as responsive as fresh ones; window still
      // grows on scroll-up so users can read full history.
      renderedMessageSize: 25,
    }
  },
  computed: {
    emojiTable() {
      return EMOJI_TABLE;
    },
    chatListeners() {
      // vue-advanced-chat v0.6.9 has a bug: it checks $listeners.textareaActionHandler (camelCase)
      // but Vue 2 stores event listeners in kebab-case. Using v-on with an object bypasses this.
      return {
        textareaActionHandler: this.onTextareaAction,
        'send-message-reaction': this.onSendMessageReaction,
      }
    },
    effectiveCurrentUserId() {
      // 🔧 FIX: Use siteID for conversations instead of individual userID
      // This ensures consistent LEFT/RIGHT alignment for all employees at the same site
      return this.siteID || this.currentUserId || this.userID
    },
    allSettings() {
      // Map from mixin.settings (array) to object keyed by Property, same as mixin does
      if (this.settings) {
        try {
          return _.keyBy(this.settings, 'Property')
        } catch(_) {}
      }
      return null
    },
    ...mapState({
      rooms: state => state.conversationsV2.rooms,
      roomsLoaded: state => state.conversationsV2.roomsLoaded,
      loadingRooms: state => state.conversationsV2.loadingRooms,
      messages: state => state.conversationsV2.messages,
      messagesLoaded: state => state.conversationsV2.messagesLoaded,
      siteID: state => state.customer.siteID,
      user: state => state.customer.user,
      patients: state => state.customer.patients
    }),
    patientsToChatWith() {
      return this.patients || []
    },
    // Submit is live only when EXACTLY ONE of the two controls is populated. The handlers below
    // keep that true by clearing the other one; this is the belt to their braces (browser autofill
    // can populate the phone box without ever firing our @input), and createRoom() still refuses a
    // both-populated submit outright rather than guessing which recipient was meant.
    canStartConversation() {
      const target = resolveNewConversationTarget({
        selectedPatient: this.selectedUsers,
        typedPhone: this.typedPhone,
        roster: this.patientsToChatWith,
      })
      // BAD_NUMBER deliberately stays ENABLED: a dead button tells the agent nothing, whereas
      // submitting a bad number gets them describeTypedPhoneProblem()'s specific sentence about
      // what is wrong with what they typed. It refuses before any POST either way.
      return target.kind !== 'EMPTY' && target.kind !== 'CONFLICT'
    },
    // New Conversation needs the practice's number (createRoom builds the thread on it), so it
    // asks hasSmsNumber — a WhatsApp-only office keeps it: the room is where its WhatsApp replies go.
    settingsReady() {
      return hasSmsNumber(this.allSettings)
    },
    // WhatsApp-only office (SMS_OUTBOUND_DISABLED): a number, but no outbound SMS. Replies go on
    // WhatsApp only and the SMS choice is hidden — siteCapability is the one rule.
    smsOutboundOff() {
      return hasSmsNumber(this.allSettings) && !isSmsEnabled(this.allSettings)
    },
    // New Conversation: form / loading / explainer card (see whatsappNewConversation.js for the rule).
    newConversationView() {
      return resolveNewConversationView({
        smsOutboundOff: this.smsOutboundOff,
        approvedStatus: this.approvedStatus,
        approvedCount: this.approvedMessages.length,
      })
    },
    // Only roles that can open My Account > WhatsApp get the button; everyone else is told who to ask.
    canAddWhatsAppMessages() {
      return canAddWhatsAppMessages(this.user && this.user.Role)
    },
    whatsappTabLocation() {
      return WHATSAPP_TAB_LOCATION
    },
    formCallout() {
      return FORM_CALLOUT
    },
    explainerCopy() {
      return {
        title: EXPLAINER_TITLE, intro: EXPLAINER_INTRO, steps: EXPLAINER_STEPS, cta: EXPLAINER_CTA,
        askAdmin: EXPLAINER_ASK_ADMIN, finePrint: EXPLAINER_FINE_PRINT, close: EXPLAINER_CLOSE,
        panelNone: PANEL_NONE_TEXT, automatic: AUTOMATIC_NOTE,
      }
    },
    // The closed-window panel's "no approved message yet" pointer (only after the list finished loading with zero).
    noApprovedNotice() {
      return showNoApprovedNotice({ approvedStatus: this.approvedStatus, approvedCount: this.approvedMessages.length })
    },
    // Imported helpers aren't reachable from a Vue 2 template — expose the banner text here.
    windowClosedHint() {
      return whatsappWindowClosedHint()
    },
    windowClosedNotice() {
      return whatsappWindowClosedNotice({ hasApproved: this.approvedMessages.length > 0 })
    },
    // Wording for the approved-message picker (imported constants are not reachable from a Vue 2 template).
    approvedText() {
      return { choose: APPROVED_CHOOSE_LABEL, caption: APPROVED_PREVIEW_CAPTION, send: APPROVED_SEND_LABEL }
    },
    // The message the panel previews and Send sends: the picked one, else the first (one message needs no picking).
    chosenApproved() {
      return resolveChosenApproved(this.approvedMessages, this.approvedPick)
    },
    // v-model for the menu. approvedPick holds the key ('' = nothing picked yet, which means the first).
    approvedChoiceKey: {
      get() { return this.chosenApproved ? this.chosenApproved.key : '' },
      set(key) { this.approvedPick = key },
    },
    // The message box is blocked (hidden + the banner shown) for a WhatsApp reply once the 24-hour window
    // has closed. Never while the thread is still loading (no messages yet is not "no inbound").
    composerBlocked() {
      return isWhatsAppComposerBlocked({
        replyChannel: this.effectiveReplyChannel,
        windowOpen: this.whatsappWindowOpen,
        messagesLoaded: this.messagesLoaded,
      })
    },
    // DEBUG: Check unread counts in rooms
    debugUnreadInfo() {
      if (this.rooms && this.rooms.length > 0) {
        const unreadRooms = this.rooms.filter(r => r.unreadCount > 0);
        const totalUnread = this.rooms.reduce((sum, r) => sum + (parseInt(r.unreadCount) || 0), 0);
        return { totalRooms: this.rooms.length, unreadRooms: unreadRooms.length, totalUnread };
      }
      return { totalRooms: 0, unreadRooms: 0, totalUnread: 0 };
    },
    unreadRoomCount() {
      if (!this.rooms) return 0;
      return this.rooms.filter(r => (parseInt(r.unreadCount) || 0) > 0).length;
    },
    visibleRooms() {
      if (this.activeFilter !== 'unread' || !this.rooms) return this.rooms;
      return this.rooms.filter(r =>
        (parseInt(r.unreadCount) || 0) > 0 || r.roomId === this.selectedRoomID
      );
    },
    windowedMessages() {
      // Cap rendered message count to avoid the per-keystroke render cascade
      // in vue-advanced-chat. New arrivals stay visible (slice from end).
      // Older history becomes accessible by scrolling up — onFetchMessages
      // grows the window. Same pattern as ChatContainer.vue.
      if (!this.messages || this.messages.length <= this.renderedMessageSize) return this.messages;
      return this.messages.slice(this.messages.length - this.renderedMessageSize);
    },
    // Unified SMS/WhatsApp thread: surface per-message channel badges when the thread
    // involves WhatsApp at all. Keyed off the ROOM's channel (sticky once WhatsApp touches
    // it) so it's robust even when the older SMS/WhatsApp messages aren't in the loaded
    // window; falls back to the loaded messages. Pure-SMS threads show no badges (no noise).
    threadInvolvesWhatsApp() {
      if (this.currentRoomObj && this.currentRoomObj.channel === 'whatsapp') return true;
      return (this.messages || []).some(m => this.messageChannel(m) === 'whatsapp');
    },
    // Only surface "For: [patient]" chips when the thread serves more than one patient
    // (a shared/family number). A single-patient thread doesn't need per-message labels.
    isFamilyThread() {
      const pats = new Set();
      (this.messages || []).forEach(m => {
        const a = m && m.attributes;
        if (a && (a.outbound === true || a.sentViaAPI === true) && a.patNum !== undefined && a.patNum !== null) {
          pats.add(String(a.patNum));
        }
      });
      return pats.size > 1;
    },
    chatTextMessages() {
      // Override the rooms-list search placeholder so staff know the search
      // matches by patient name (first or last) — the library default of
      // "Search" implied full-text message search, which we don't support.
      return { SEARCH: 'Search by patient name' };
    },
    smsPhoneNumber() {
      if (this.allSettings && this.allSettings.GENERAL_PHONE && this.allSettings.GENERAL_PHONE.PropertyValue) {
        const raw = this.allSettings.GENERAL_PHONE.PropertyValue.trim();
        // Format +1XXXXXXXXXX to (XXX) XXX-XXXX
        const digits = raw.replace(/\D/g, '');
        if (digits.length === 11 && digits.startsWith('1')) {
          return `(${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7, 11)}`;
        }
        if (digits.length === 10) {
          return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
        }
        return raw;
      }
      return null;
    },
    siteName() {
      return this.user?.SiteName || 'Your practice';
    },
    // True when this site files inbound patient photos into Open Dental imaging. The backend gates
    // that on SMS_PHOTO_IMAGING_CATEGORY (conversations-service inhouse.pushImagesToOpenDental) —
    // a site with no category configured sends NOTHING to imaging, so the note must not promise it.
    // Prod 2026-08-25: 4 of the 5 sites receiving MMS have it set; Batavia does not.
    photosSaveToOpenDental() {
      const setting = this.allSettings && this.allSettings.SMS_PHOTO_IMAGING_CATEGORY;
      return !!(setting && String(setting.PropertyValue || '').trim());
    },
    // Staff-facing note about the per-text photo cap. Without it, a patient sending three photos
    // looks like we dropped one. Wording stays plain — no jargon, and no claim we can't back.
    photoDisplayNote() {
      const shown = `Up to ${MAX_RENDERED_MEDIA_PER_MESSAGE} photos per text are shown here`;
      return this.photosSaveToOpenDental
        ? `${shown} — every photo a patient sends is saved to their Open Dental imaging.`
        : `${shown}. To also file patient photos into Open Dental, set an imaging category in Conversation Settings.`;
    },
    // Demo site (Task 6.4): a demo session also gets "Simulate a patient reply"; everyone else, this exact list.
    roomActions() {
      return demoRoomActions([{ name: 'markUnread', title: 'Mark as Unread' }], this.$store.getters['customer/isDemo'] === true)
    },
    // GROUP MMS: Check if currently selected room is a Group MMS conversation
    // This is used to show warning banner when staff is about to reply to a group conversation
    isCurrentRoomGroupMMS() {
      if (!this.selectedRoomID || !this.rooms || this.rooms.length === 0) {
        return false
      }
      const currentRoom = this.rooms.find(r => r.roomId === this.selectedRoomID)
      return currentRoom && currentRoom.isGroupMMS === true
    },
    // WhatsApp channel context for the selected conversation (additive — SMS rooms unaffected).
    currentRoomObj() {
      if (!this.selectedRoomID || !this.rooms) return null
      return this.rooms.find(r => r.roomId === this.selectedRoomID) || null
    },
    // Thread-header half of the "Not in Open Dental" badge. The predicate is shared with the
    // room-row pill (utils/patientPhone.js) so the two can never disagree about one thread.
    currentRoomNotInOpenDental() {
      return threadIsNotInOpenDental(this.currentRoomObj)
    },
    // Does this site offer WhatsApp? Gates the reply-channel selector + per-message badges.
    siteWhatsAppEnabled() {
      const v = this.allSettings && this.allSettings.WHATSAPP_ENABLED && this.allSettings.WHATSAPP_ENABLED.PropertyValue
      return String(v).toLowerCase() === 'true'
    },
    // Smart default: reply on whatever channel the patient last used (else SMS).
    defaultReplyChannel() {
      return lastInboundChannel(this.messages) || 'sms'
    },
    // The channel the reply actually goes out on: explicit agent override, else the default.
    effectiveReplyChannel() {
      return resolveReplyChannel({
        siteWhatsAppEnabled: this.siteWhatsAppEnabled,
        smsOutboundOff: this.smsOutboundOff,
        override: this.replyChannelOverride,
        defaultChannel: this.defaultReplyChannel,
      })
    },
    // Whether the SMS-guidance bar is currently showing (SMS reply channel + a room selected +
    // some composer text). Drives the bar's v-if; placeGuidanceBar() moves it under the composer.
    showComposerGuidance() {
      return !!(this.selectedRoomID && this.effectiveReplyChannel === 'sms'
        && this.composerText && this.composerText.trim())
    },
    // WhatsApp 24h window, measured from the last inbound WHATSAPP message specifically
    // (a later SMS must NOT re-open the WhatsApp window).
    // Reads the ticking nowMs (refreshed every minute) so a window that runs out while the thread is open
    // closes on screen, instead of staying "open" until some message changes.
    whatsappWindowOpen() {
      return isWithinWhatsAppWindow(lastWhatsAppInboundMs(this.messages), this.nowMs)
    }
  },
  created() {
    // Follow Huddle pattern: only initialize after settings are available
    if (this.allSettings) {
      try { this.$store.dispatch('conversationsV2/initUnreadBaseline') } catch(_) {}
      try { this.$store.dispatch('conversationsV2/loadLastReadMap') } catch(_) {}
      this.initV2()
      this.logSettingsSnapshot('created: allSettings present')
    }
  },
  mounted() {
    try {
      this.logSettingsSnapshot('mounted')
      // In case settings are already present on mount but created hook didn't run after mixin
      if (this.allSettings && !this.initialized) {
        try { this.$store.dispatch('conversationsV2/initUnreadBaseline') } catch(_) {}
        try { this.$store.dispatch('conversationsV2/loadLastReadMap') } catch(_) {}
        this.initV2()
      }
      this.setupFileClickInterception()
      this.setupRoomsDeselect()
      this.$nextTick(() => this.applyWhatsAppRoomStyles())
      this.$nextTick(() => this.applyNotInOpenDentalRoomStyles())
      // Attach the composer-text mirror as soon as the textarea exists (retries internally).
      this.$nextTick(() => this.bindComposerInput())
      // Add keyboard listener for ESC key to close modal
      document.addEventListener('keydown', this.handleKeyDown)
      // Keep the composer inside the viewport: refit on window resize and whenever content above the
      // chat appears/disappears — this page's own (new-conversation form, group banner, Reply-via bar)
      // AND the layout's, which render outside this component and arrive after mount (desktop-alerts
      // prompt, tip carousel). Those live in Layout's `.content`, so observe that. NOT document.body:
      // body is pinned to the viewport height and is itself the scroller, so it never resizes.
      window.addEventListener('resize', this.fitChatToViewport)
      // Let the WhatsApp 24-hour window close on screen when its time runs out (see whatsappWindowOpen).
      this.nowTimer = setInterval(() => { this.nowMs = Date.now() }, 60 * 1000)
      if (typeof ResizeObserver !== 'undefined') {
        // Defer to the next frame: resizing the chat inside the callback resizes the observed element in
        // the same frame, which raises "ResizeObserver loop completed with undelivered notifications" —
        // and captureFormLogs reports that as an error.
        this.chatFitObserver = new ResizeObserver(() => {
          if (this.chatFitFrame) cancelAnimationFrame(this.chatFitFrame)
          this.chatFitFrame = requestAnimationFrame(() => { this.chatFitFrame = null; this.fitChatToViewport() })
        })
        this.chatFitObserver.observe(this.$el.closest('.content') || this.$el)
      }
      this.$nextTick(() => this.fitChatToViewport())
      // Auto-open settings panel if URL says so (used by FeatureNudge CTAs)
      this.$nextTick(() => this.maybeAutoOpenSettings())
      // Open the thread named by ?roomId= (Action Center "View conversation", dashboard Unread Messages)
      this.$nextTick(() => this.maybeOpenRoomFromQuery())
    } catch (e) { console.error('[ConversationsV2] init error', e) }
  },
  beforeDestroy() {
    if (this.nowTimer) { clearInterval(this.nowTimer); this.nowTimer = null }
    // Only detach page-specific listeners (message listener etc).
    // The rooms listener is kept alive so the sidebar unread indicator
    // continues receiving real-time updates after navigating away.
    // Full teardown (detachAll) happens on logout and app unmount via Layout.vue.
    this.$store.dispatch('conversationsV2/detachPageListeners')
    // Remove the WhatsApp room-row icon stylesheet on leave.
    try { const _wa = document.getElementById('wa-room-styles'); if (_wa) _wa.remove(); } catch (_) {}
    // Same for the "Not in Open Dental" room-row pill stylesheet — it lives on document.head,
    // outside this component, so nothing else tears it down.
    try { const _od = document.getElementById('not-in-od-room-styles'); if (_od) _od.remove(); } catch (_) {}
    // Clear the selected room when leaving the Conversations page.
    // Otherwise the room-ingest unread calc keeps forcing unreadCount=0
    // for the previously-selected room (its `isSelected` check), which
    // suppresses the sidebar green dot for new inbound SMS to that room
    // even though the user is no longer viewing it. (Reported in PROD
    // 2026-04-29: user A's machine had a room selected before navigating
    // away → no green dot for new SMS; user B on a different machine
    // with no selection saw the green dot fine.)
    this.$store.commit('conversationsV2/setSelectedRoomId', null)
    // Clean up event listener
    document.removeEventListener('click', this.handleDocumentClick, true)
    // Clean up keyboard listener
    document.removeEventListener('keydown', this.handleKeyDown)
    window.removeEventListener('resize', this.fitChatToViewport)
    if (this.chatFitObserver) { this.chatFitObserver.disconnect(); this.chatFitObserver = null }
    if (this.chatFitFrame) { cancelAnimationFrame(this.chatFitFrame); this.chatFitFrame = null }
  },
  methods: {
    // Per-message channel ('sms' default — absent on legacy/SMS docs; 'whatsapp' when stamped
    // by the engine). Drives the channel badge in a mixed thread.
    messageChannel(m) {
      return (m && m.channel === 'whatsapp') ? 'whatsapp' : 'sms';
    },
    // The patient an OUTBOUND message was meant for (engine stamps attributes.patNum/patientName
    // = the triggering appointment/recall/campaign patient). Drives the "For: [patient]" chip.
    messageForLabel(m) {
      const a = m && m.attributes;
      const outbound = a && (a.outbound === true || a.sentViaAPI === true);
      return outbound && a.patientName ? a.patientName : null;
    },
    isImageMessage(m) {
      try {
        const f = (m && m.file) || (m && Array.isArray(m.files) && m.files[0]) || null;
        if (!f) return false;
        const url = realFileUrl(f);  // proxy /message-media URLs count as no url → not an image
        const ext = (function(t, n){
          try {
            if (typeof t === 'string' && t.includes('/')) return t.split('/')[1];
            if (typeof n === 'string' && n.includes('.')) return n.split('.').pop();
            return t || '';
          } catch(_) { return t || ''; }
        })(f.type || f.mime, f.name);
        const lower = String(ext || '').toLowerCase();
        const byExt = ['jpg','jpeg','png','gif','webp','bmp','svg'].includes(lower);
        const byMime = (f.type || f.mime || '').toLowerCase().startsWith('image/');
        return (!!url) && (byExt || byMime || f.image === true);
      } catch(_) { return false; }
    },
    isLegacyExpiredMedia(m) {
      // Legacy Twilio Conversations MMS (media SID, no real S3 url) that can no longer be served →
      // render a placeholder instead of an auto-loading <img>/link that would hit the retired proxy.
      return legacyExpiredMedia(m);
    },
    isFailedMessage(m) {
      // Check if message failed to send
      return m?.attributes?.failed === true || m?.failed === true;
    },
    isPendingMessage(m) {
      // Check if message is still being sent (optimistic, not confirmed)
      return m?.attributes?.optimistic === true && !m?.attributes?.failed;
    },
    retryMessage(m) {
      // Emit event to retry sending the message
      // For now, show an alert - could be enhanced to actually retry
      this.$message?.warning?.('Please try sending the message again') ||
        alert('Please try sending the message again');
    },
    imageSrc(m) {
      try {
        const f = (m && m.file) || (m && Array.isArray(m.files) && m.files[0]) || null;
        // Real S3/blob URL only — never the dead /message-media proxy (legacy Twilio Conversations).
        return realFileUrl(f);
      } catch(_) { return ''; }
    },
    onImgLoadStart(m, ev) {
      try {
        const messageId = m && (m._id || m.id);
        if (messageId) {
          // Set loading state to true when image starts loading
          this.$set(this.imageLoadingStates, messageId, true);
        }
      } catch(_) {}
    },
    onImgError(m, ev) {
      try {
        const messageId = m && (m._id || m.id);
        if (messageId) {
          // Clear loading state on error
          this.$set(this.imageLoadingStates, messageId, false);
        }
      } catch(_) {}
    },
    onImgLoad(m, ev) {
      try {
        const messageId = m && (m._id || m.id);
        if (messageId) {
          // Clear loading state when image loads successfully
          this.$set(this.imageLoadingStates, messageId, false);
        }
      } catch(_) {}
    },
    getFileUrl(m) {
      try {
        const f = (m && m.file) || (m && Array.isArray(m.files) && m.files[0]) || null;
        if (!f) return '#';
        // Real S3/blob URL only. Legacy Twilio Conversations media (mediaSid, no S3 url) is no longer
        // servable — the /message-media proxy is retired — so return '#' and let callers no-op.
        return realFileUrl(f) || '#';
      } catch(_) { return '#'; }
    },
    getFileName(m) {
      try {
        const f = (m && m.file) || (m && Array.isArray(m.files) && m.files[0]) || null;
        return (f && f.name) || 'Attachment';
      } catch(_) { return 'Attachment'; }
    },
    openImageModal(m) {
      try {
        const src = this.imageSrc(m);
        if (!src) {
          return;
        }
        const filename = (m && m.file && m.file.name) || 'image';
        this.expandedImage = {
          src: src,
          alt: filename,
          filename: filename
        };
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
      } catch (error) {
        console.error('[ConversationsV2] Error opening image modal:', error);
      }
    },
    closeImageModal() {
      this.expandedImage = null;
      // Restore body scroll
      document.body.style.overflow = '';
    },
    handleKeyDown(event) {
      // Close modal on ESC key
      if (event.key === 'Escape' && this.expandedImage) {
        this.closeImageModal();
        return;
      }
    },
    onModalImageLoad(event) {
      // Image loaded successfully in modal
    },

    setupFileClickInterception() {
      // Wait for DOM to be ready
      this.$nextTick(() => {
        // Use event delegation to capture clicks on file elements
        document.addEventListener('click', this.handleDocumentClick, true);
      });
    },
    handleDocumentClick(event) {
      try {
        // Check if the clicked element is within a vue-advanced-chat file message
        const fileElement = event.target.closest('.vac-file-message');
        if (!fileElement) return;

        // Find the message container to get the message ID
        const messageContainer = event.target.closest('[id^="IM"]');
        if (messageContainer) {
          const messageId = messageContainer.id;

          // Find the message in our data
          const message = this.messages.find(m => (m._id || m.id) === messageId);
          if (message) {
            // Prevent the default behavior and download the file
            event.preventDefault();
            event.stopPropagation();

            this.downloadFile(message);
          }
        }
      } catch (error) {
        console.error('[ConversationsV2] Error in handleDocumentClick:', error);
      }
    },
    handleOpenFile({ message, action } = {}) {
      // Wires up vue-advanced-chat's built-in image buttons (.vac-image-buttons):
      // the eye icon emits open-file with action 'preview', the arrow with 'download'.
      if (!message) return;
      if (action === 'download') {
        this.downloadFile(message);
      } else {
        // 'preview' (eye icon): open full-resolution image in a new tab so users
        // get native browser zoom/pan to read fine print (e.g. insurance cards).
        const url = this.getFileUrl(message);
        if (url && url !== '#') {
          window.open(url, '_blank', 'noopener');
        }
      }
    },
    async downloadFile(m) {
      try {
        const url = this.getFileUrl(m);

        if (!url || url === '#') {
          alert('Error: No valid URL for file download');
          return;
        }

        // Get authentication token
        const token = localStorage.getItem('id_token');
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Make authenticated request
        const response = await axios.get(url, {
          headers,
          responseType: 'blob',
          timeout: 30000
        });

        // Create blob URL and trigger download
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/octet-stream'
        });

        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = this.getFileName(m);

        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up blob URL
        window.URL.revokeObjectURL(blobUrl);

      } catch (error) {
        console.error('[ConversationsV2] File download failed:', error);
        
        // Show user-friendly error
        if (error.response?.status === 403) {
          alert('Access denied. You may not have permission to download this file.');
        } else if (error.response?.status === 404) {
          alert('File not found. The file may have been deleted or moved.');
        } else if (error.code === 'NETWORK_ERROR' || error.message.includes('timeout')) {
          alert('Network error. Please check your connection and try again.');
        } else {
          alert('Failed to download file. Please try again.');
        }
      }
    },
    logSettingsSnapshot(contextLabel) {
      // Settings snapshot logging removed for production
    },
    // DEBUG: Manual trigger for unread debugging
    debugUnreadState() {
      // Debug logging removed for production
    },
    initV2() {
      if (this.initialized) return
      
      // Check if conversations listener is already active (from Layout component)
      const isListenerActive = this.$store.state.conversationsV2?.roomsListener;
      
      if (isListenerActive) {
        this.initialized = true
      } else {
        this.$store.dispatch('conversationsV2/initRoomsListener', { siteID: this.siteID })
        this.initialized = true
      }
      
      // Make debug method available globally for console debugging
      if (typeof window !== 'undefined') {
        window.debugConversationsUnread = () => this.debugUnreadState();
      }
    },
    ...mapActions('conversationsV2', ['fetchMessages', 'fetchSmsOptOutStatus']),
    async markRoomRead(roomId) {
      try {
        // Determine last viewed message id from current V2 messages
        const last = (this.messages && this.messages.length > 0) ? this.messages[this.messages.length - 1] : null;
        const lastViewedMessageId = last && (last._id || last.id || last.messageSID) || null;
        // Record client-side lastRead timestamp for immediate per-room badge accuracy
        const lastMs = last && (last.tsNum || (new Date()).getTime());
        try { this.$store.dispatch('conversationsV2/recordRoomRead', { roomId, ms: lastMs }); } catch(_) {}
        // Hit backend to reset unreadCount and record last viewed
        await this.$store.dispatch('conversationsV2/updateConversationReadStatus', {
          conversationId: roomId,
          lastViewedMessageId
        });
        // Refresh sidebar badge state
        await this.$store.dispatch('conversationsV2/updateSidebarNotification');
      } catch (e) { /* markRoomRead error */ }
    },
    async onRoomActionHandler({ action, roomId }) {
      if (action.name === 'markUnread') {
        try {
          // Clear local selected room so vue-advanced-chat deselects it
          this.selectedRoomID = '';
          await this.$store.dispatch('conversationsV2/markConversationUnread', { roomId });
          this.$toast.success('Conversation marked as unread');
        } catch (err) {
          this.$toast.error('Failed to mark conversation as unread');
        }
      }
      if (action.name === SIMULATE_REPLY_ACTION.name) {
        try {
          await this.$store.dispatch('customer/demoSimulateReply', { conversationId: roomId });
          this.$toast.info('The sample patient replied.');
        } catch (err) {
          this.$toast.error(demoErrorMessage(err, 'simulate'));
        }
      }
    },
    setupRoomsDeselect() {
      this.$nextTick(() => {
        const roomsContainer = this.$el.querySelector('.vac-rooms-container');
        if (roomsContainer) {
          roomsContainer.addEventListener('click', (e) => {
            // Only deselect if clicking empty space, not on a room item
            if (!e.target.closest('.vac-room-item')) {
              this.deselectRoom();
            }
          });
        }
      });
    },
    deselectRoom() {
      this.selectedRoomID = '';
      try { this.$store.commit('conversationsV2/setSelectedRoomId', '') } catch(_) {}
      // Reset vue-advanced-chat internal room state (library ignores empty roomId prop)
      const chatWindow = this.$el.querySelector('.vac-card-window');
      if (chatWindow && chatWindow.__vue__) {
        chatWindow.__vue__.room = {};
      }
    },
    onRoomSelected(room) {
      if (room && room.roomId) {
        this.selectedRoomID = room.roomId
        this.composerText = '' // fresh guidance for the new thread
        this.$nextTick(() => this.bindComposerInput())

        try { this.$store.commit('conversationsV2/setSelectedRoomId', room.roomId) } catch(_) {}
        // Optimistically clear unread immediately and set local lastRead to now
        try {
          this.$store.commit('conversationsV2/setRoomUnreadCount', { roomId: room.roomId, count: 0 })
          this.$store.dispatch('conversationsV2/recordRoomRead', { roomId: room.roomId, ms: Date.now() })
          this.$store.dispatch('conversationsV2/updateSidebarNotification')
        } catch(_) {}
        this.fetchMessages({ roomId: room.roomId, messagesPerPage: 20 }).then(() => {
          this.markRoomRead(room.roomId)
        })
      }
    },
    // The guidance bar is rendered in the template (Vue needs an anchor) but belongs UNDER the
    // composer, which vue-advanced-chat owns. Relocate it to just after the composer box
    // (.vac-box-footer), inside .vac-room-footer — the same technique renderRecentBar() uses for
    // the Quick Actions bar — so it lines up with the composer instead of floating full-width
    // below the whole card. Idempotent: a no-op once it's already in place.
    placeGuidanceBar() {
      const bar = this.$el && this.$el.querySelector('.conv-sms-guidance-bar');
      if (!bar) return; // not rendered yet (no room selected) — nothing to place
      const footer = this.$el.querySelector('.vac-box-footer');
      if (!footer || !footer.parentElement) return;
      if (footer.nextSibling !== bar) {
        footer.parentElement.insertBefore(bar, footer.nextSibling);
      }
    },
    // Mirror the chat textarea's live text into composerText so <SmsGuidance> can warn/block
    // in this composer too (vue-advanced-chat owns the textarea and emits no text event, so we
    // listen on the DOM). Bound once per textarea element; the block-restore's dispatched
    // 'input' event flows through here as well, keeping the banner in sync after a blocked send.
    // Called from multiple entry points (mount, both room-selection paths) since vue-advanced-chat
    // renders the textarea asynchronously — the _smsGuidanceBound guard makes re-calls idempotent.
    // Size the chat so its bottom (the composer) lands inside the viewport at scroll 0. Measured in
    // PAGE coordinates so the result doesn't depend on the current scroll position. Only writes when
    // the value changes, so the ResizeObserver it feeds settles instead of looping.
    fitChatToViewport() {
      const win = this.$el && this.$el.querySelector('.vac-card-window');
      if (!win) return;
      const rect = win.getBoundingClientRect();
      // Hidden behind the loading skeleton (v-show) every coordinate reads 0 → would size to ~100vh.
      if (!rect.height) return;
      // The layout scrolls <body>, not the window, so window.scrollY stays 0 — add both.
      const scrolled = (window.scrollY || 0) + ((document.body && document.body.scrollTop) || 0);
      const pageTop = rect.top + scrolled;
      const next = chatFitHeight(window.innerHeight, pageTop) + 'px';
      if (next !== this.chatHeight) this.chatHeight = next;
    },
    bindComposerInput(attempt = 0) {
      const ta = this.$el && this.$el.querySelector('.vac-textarea');
      if (!ta) { if (attempt < 5) setTimeout(() => this.bindComposerInput(attempt + 1), 200); return; }
      this.placeGuidanceBar(); // wrapper exists once a room is selected — position it under the composer
      this.placeClosedPanel(); // the closed-window panel lives in the same footer
      if (ta._smsGuidanceBound) return;
      ta._smsGuidanceBound = true;
      ta.addEventListener('input', (e) => {
        this.composerText = (e && e.target && e.target.value) || '';
        // Self-heal placement if vue-advanced-chat re-rendered its footer; idempotent no-op otherwise.
        this.$nextTick(() => this.placeGuidanceBar());
      });
    },
    onFetchMessages({ room, options }) {
      if (Date.now() < this.suppressAutoFetchUntil) {
        return
      }
      if (room && room.roomId) {
        // Reset rendered window on room switch so the new room starts capped
        if (this.selectedRoomID !== room.roomId) {
          this.renderedMessageSize = 25
        }
        // Treat fetch-messages as selection for VAC v0.6.3 (sometimes room-selected isn't emitted)
        if (this.selectedRoomID !== room.roomId) {
          this.composerText = '' // fresh guidance for the new thread (room-selected may not fire)
        }
        this.selectedRoomID = room.roomId
        this.$nextTick(() => this.bindComposerInput())

        try { this.$store.commit('conversationsV2/setSelectedRoomId', room.roomId) } catch(_) {}
        try {
          this.$store.commit('conversationsV2/setRoomUnreadCount', { roomId: room.roomId, count: 0 })
          this.$store.dispatch('conversationsV2/recordRoomRead', { roomId: room.roomId, ms: Date.now() })
          this.$store.dispatch('conversationsV2/updateSidebarNotification')
        } catch(_) {}

        // If older messages are already in Vuex above the rendered window,
        // expand the window first instead of refetching. The library fires
        // fetch-messages on scroll-to-top; reveal already-loaded older
        // messages before going back to the network.
        const msgsBefore = this.messages || []
        if (msgsBefore.length > this.renderedMessageSize) {
          this.renderedMessageSize = Math.min(msgsBefore.length, this.renderedMessageSize + 50)
          return
        }

        this.fetchMessages({ roomId: room.roomId, messagesPerPage: 20 }).then(() => {
          try { this.markRoomRead(room.roomId) } catch(_) {}
          // After fetching from Firestore, ensure newly loaded older messages
          // are inside the rendered window.
          if ((this.messages || []).length > this.renderedMessageSize) {
            this.renderedMessageSize = (this.messages || []).length
          }
          try {
            // Seed debug image with last message file URL if present
            const msgs = this.messages || []
            const latest = msgs.length ? msgs[msgs.length - 1] : null
            const f = latest && (latest.file || (Array.isArray(latest.files) && latest.files[0]))
            const u = f && (f.url || f.localUrl)
            this.debugImageUrl = u || ''
          } catch(_) {}
          // Inline image rendering is handled declaratively by the per-message
          // `message_<id>` slot (see template). The old imperative renderInlineImages()
          // DOM-injection fallback was removed — it ran alongside the slot and
          // rendered every inbound image twice.
        })
      }
    },
    onTextareaAction() {
      // Called when the custom textarea action icon is clicked
      this.showTemplateModal = true;
    },
    // Agent explicitly picks the reply channel for this thread (overrides the smart default).
    setReplyChannel(ch) {
      this.replyChannelOverride = ch;
    },
    // Mark WhatsApp threads in the room list with a green WhatsApp icon. vue-advanced-chat 0.6.9
    // has no per-row slot we can use non-destructively, BUT it stamps each room's id on its
    // .vac-room-item element — so we target those ids with a CSS ::before glyph (FontAwesome
    // \f232). Using a stylesheet (not injected DOM nodes) means it survives the library's
    // re-renders. Regenerated whenever the room set changes.
    applyWhatsAppRoomStyles() {
      let styleEl = document.getElementById('wa-room-styles');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'wa-room-styles';
        document.head.appendChild(styleEl);
      }
      const ids = (this.rooms || []).filter(r => r && r.channel === 'whatsapp' && r.roomId).map(r => r.roomId);
      if (!ids.length) { styleEl.textContent = ''; return; }
      const esc = (s) => (window.CSS && CSS.escape) ? CSS.escape(s) : String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
      const sel = ids.map(id => `.vac-room-item#${esc(id)} .vac-room-name::before`).join(',');
      styleEl.textContent = `${sel}{content:"\\f232";font-family:"FontAwesome";color:#25d366;margin-right:6px;font-weight:400;}`;
    },
    // Room-row half of the "Not in Open Dental" badge. Same constraint as the WhatsApp icon above:
    // vue-advanced-chat 0.6.9 has no per-room slot, but it stamps each room's id on its
    // .vac-room-item element — so the pill is a ::after on a selector list keyed by room id, in one
    // stylesheet that survives the library's re-renders. The verdict comes from the SAME predicate
    // the header banner uses, so a thread can never be badged in one place and not the other.
    applyNotInOpenDentalRoomStyles() {
      let styleEl = document.getElementById('not-in-od-room-styles');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'not-in-od-room-styles';
        document.head.appendChild(styleEl);
      }
      const ids = (this.rooms || []).filter(r => r && r.roomId && threadIsNotInOpenDental(r)).map(r => r.roomId);
      // Clear rather than leave the previous set in place: a thread that self-heals (the number
      // gets added to Open Dental) has to LOSE the pill without a page reload.
      if (!ids.length) { styleEl.textContent = ''; return; }
      const esc = (s) => (window.CSS && CSS.escape) ? CSS.escape(s) : String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
      const sel = ids.map(id => `.vac-room-item#${esc(id)} .vac-room-name::after`).join(',');
      styleEl.textContent = `${sel}{content:"Not in OD";display:inline-block;margin-left:6px;padding:1px 6px;border-radius:9px;background:#eef2f7;color:#5b6b7f;font-size:10px;font-weight:600;line-height:1.5;vertical-align:middle;white-space:nowrap;}`;
    },
    async onSendMessageReaction({ reaction, remove, messageId, roomId }) {
      try {
        await this.$store.dispatch('conversationsV2/sendMessageReaction', { reaction, remove, messageId, roomId });
        if (!remove && reaction && reaction.unicode) {
          this.trackRecentEmoji(reaction.unicode);
        }
      } catch (err) {
        console.error('Reaction failed to save', err);
      }
    },
    insertTextAtCursor(text) {
      const textarea = this.$el.querySelector('.vac-textarea');
      if (!textarea) return;
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const current = textarea.value;
      textarea.value = current.substring(0, start) + text + current.substring(end);
      // Dispatch input event so vue-advanced-chat syncs its internal state
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
      // Place cursor right after the inserted text
      const newPos = start + text.length;
      textarea.selectionStart = newPos;
      textarea.selectionEnd = newPos;
      // Auto-resize and focus
      textarea.style.height = '0';
      textarea.style.height = textarea.scrollHeight + 'px';
      textarea.focus();
    },
    insertVariable(varName) {
      this.insertTextAtCursor(`{{${varName}}}`);
      this.trackRecentVariable(varName);
    },
    insertEmoji(emoji) {
      this.insertTextAtCursor(emoji);
      this.trackRecentEmoji(emoji);
    },
    onTemplateSelected(templateData) {
      this.showTemplateModal = false;
      // Store link map for form link restoration before sending
      this.templateLinkMap = templateData.linkMap || {};
      this.templateWikiPageID = templateData.wikiPageID || null;
      this.trackRecentTemplate(templateData);
      // Use room-message prop to prefill textarea via vue-advanced-chat API
      this.prefillRoomMessage = templateData.content;
      // Clear prefill after vue-advanced-chat picks it up, then trigger resize
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.prefillRoomMessage = null;
          // Trigger textarea auto-resize for long template content
          const textarea = this.$el.querySelector('.vac-textarea');
          if (textarea) {
            textarea.style.height = '0';
            textarea.style.height = textarea.scrollHeight + 'px';
            textarea.focus();
          }
        });
      });
    },

    // =====================================================
    // RECENTLY USED BAR — tracking + rendering
    // =====================================================
    trackRecentEmoji(emoji) {
      let recents = [...this.recentEmojis];
      recents = recents.filter(e => e !== emoji);
      recents.unshift(emoji);
      this.recentEmojis = recents.slice(0, 10);
      localStorage.setItem('teamio-sms-recent-emojis', JSON.stringify(this.recentEmojis));
      this.renderRecentBar();
    },
    trackRecentVariable(varName) {
      const recents = this.recentVariables.filter(v => v !== varName);
      recents.unshift(varName);
      this.recentVariables = recents.slice(0, 6);
      localStorage.setItem('teamio-sms-recent-variables', JSON.stringify(this.recentVariables));
      this.renderRecentBar();
    },
    trackRecentTemplate(templateData) {
      const title = templateData.title || templateData.content.substring(0, 30);
      const id = templateData.wikiPageID || title;
      const recents = this.recentTemplates.filter(t => t.id !== id);
      recents.unshift({
        id,
        title,
        content: templateData.content || '',
        linkMap: templateData.linkMap || {},
        wikiPageID: templateData.wikiPageID || null
      });
      this.recentTemplates = recents.slice(0, 6);
      localStorage.setItem('teamio-sms-recent-templates', JSON.stringify(this.recentTemplates));
      this.renderRecentBar();
    },
    insertRecentTemplate(tmpl) {
      this.templateLinkMap = tmpl.linkMap || {};
      this.templateWikiPageID = tmpl.wikiPageID || null;
      // Directly set textarea content (bypasses prefillRoomMessage timing issues)
      const textarea = this.$el.querySelector('.vac-textarea');
      if (textarea) {
        textarea.value = tmpl.content;
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
        textarea.style.height = '0';
        textarea.style.height = textarea.scrollHeight + 'px';
        textarea.focus();
      }
    },
    renderRecentBar() {
      // Vue renders the + Insert dropdown inside .conversations-page; we relocate its DOM
      // node into the recent-bar so layout is responsive (no absolute positioning).
      // Detach it from any previous bar BEFORE we destroy that bar, otherwise .remove()
      // would take the dropdown down with it.
      const insertEl = this.$el.querySelector('.insert-button-wrapper');
      if (insertEl && this.recentBarEl && this.recentBarEl.contains(insertEl)) {
        this.$el.appendChild(insertEl);
      }

      // Remove old bar
      if (this.recentBarEl) {
        this.recentBarEl.remove();
        this.recentBarEl = null;
      }
      const footer = this.$el.querySelector('.vac-box-footer');
      if (!footer || !footer.parentElement) return;

      const container = document.createElement('div');
      container.className = 'recent-bar';

      // + Insert dropdown (left side of the bar)
      if (insertEl) {
        container.appendChild(insertEl);
      }

      // Header label
      const header = document.createElement('span');
      header.className = 'recent-bar__header';
      header.textContent = 'Quick Actions';
      header.title = 'Recently used emojis, variables, and templates — click any to insert';
      container.appendChild(header);

      // Build segmented bar: emojis | variables | templates
      const items = document.createElement('div');
      items.className = 'recent-bar__items';

      // Emoji section
      if (this.recentEmojis.length) {
        this.recentEmojis.slice(0, 8).forEach(emoji => {
          const btn = document.createElement('span');
          btn.className = 'recent-btn recent-btn--emoji';
          btn.textContent = emoji;
          btn.title = emoji;
          btn.addEventListener('click', () => this.insertEmoji(emoji));
          items.appendChild(btn);
        });
      }

      // Divider + Variables section
      if (this.recentVariables.length) {
        const sep = document.createElement('span');
        sep.className = 'recent-bar__divider';
        items.appendChild(sep);

        this.recentVariables.slice(0, 4).forEach(varName => {
          const btn = document.createElement('span');
          btn.className = 'recent-btn recent-btn--variable';
          btn.textContent = VARIABLE_LABELS[varName] || varName;
          btn.title = '{{' + varName + '}}';
          btn.addEventListener('click', () => this.insertVariable(varName));
          items.appendChild(btn);
        });
      }

      // Divider + Templates section
      if (this.recentTemplates.length) {
        const sep = document.createElement('span');
        sep.className = 'recent-bar__divider';
        items.appendChild(sep);

        this.recentTemplates.slice(0, 4).forEach(tmpl => {
          const btn = document.createElement('span');
          btn.className = 'recent-btn recent-btn--template';
          btn.innerHTML = '<i class="fa fa-file-text-o"></i> ' + (tmpl.title.length > 20 ? tmpl.title.substring(0, 20) + '…' : tmpl.title);
          btn.title = tmpl.content.length > 80 ? tmpl.content.substring(0, 80) + '…' : tmpl.content;
          btn.addEventListener('click', () => this.insertRecentTemplate(tmpl));
          items.appendChild(btn);
        });
      }

      container.appendChild(items);
      footer.parentElement.insertBefore(container, footer);
      this.recentBarEl = container;

      // Add tooltips to vue-advanced-chat footer icons (rendered by the library)
      // Buttons inside .vac-icon-textarea in order: [paperclip] [SMS templates] [send]
      try {
        const btns = Array.from(footer.querySelectorAll('.vac-icon-textarea .vac-svg-button'));
        btns.forEach((btn, i) => {
          if (btn.getAttribute('title')) return; // already has tooltip (e.g. SMS templates via slot)
          if (i === btns.length - 1) {
            btn.setAttribute('title', 'Send message');
          } else {
            btn.setAttribute('title', 'Attach file');
          }
        });
      } catch (_) {}
    },

    // vue-advanced-chat clears its own textarea ~25ms after Send is clicked (past any Vue tick),
    // so a message we refuse to send (blocked, or a cancelled cost-confirm) would vanish and force
    // a full retype. Put the text back so the user can just adjust it — on a short macrotask timeout
    // so it lands AFTER vac's clear (a $nextTick fires too early and gets wiped). Also re-syncs the
    // guidance banner via the dispatched 'input' event.
    restoreComposerText(text) {
      const preserved = text;
      setTimeout(() => {
        const textarea = this.$el && this.$el.querySelector('.vac-textarea');
        if (textarea) {
          textarea.value = preserved;
          textarea.dispatchEvent(new Event('input', { bubbles: true }));
          textarea.style.height = '0';
          textarea.style.height = textarea.scrollHeight + 'px';
          textarea.focus();
        }
      }, 60);
    },
    async onSendMessage({ roomId, content, file, replyMessage, options }) {
      try {
        if (!roomId || (((!content || !content.trim()) && !file))) {
          this.$toast && this.$toast.error('Message is empty')
          return
        }

        // Channel this reply goes out on: the agent's explicit selector, else the smart
        // default (patient's last-inbound channel). For SMS-only sites this is always 'sms'.
        const replyChannel = this.effectiveReplyChannel;
        // WhatsApp only allows free-form replies inside the 24h customer-service window —
        // block and guide the agent rather than letting the send fail downstream (error 63016).
        if (replyChannel === 'whatsapp' && !this.whatsappWindowOpen) {
          this.$toast && this.$toast.error(whatsappWindowClosedToast());
          return
        }

        // Shared SMS guidance (utils/smsGuidance): block an over-carrier-limit SMS before it
        // goes out — carriers won't reliably deliver it and you're billed for the failed
        // attempt. SMS channel only (WhatsApp / in-house chat aren't segment-capped the same
        // way); MMS (a file attachment) is never blocked. Measure the raw `content` (not the
        // form-link-expanded finalContent) so HTML-anchor expansion can't false-block.
        // Hoisted so the optimistic credit draw below can read the SMS segment count (it's the
        // charge amount for an SMS send). Stays null for WhatsApp / in-house chat.
        let smsGuidance = null;
        if (replyChannel === 'sms') {
          smsGuidance = getSmsGuidance(content || '', { media: file ? [file] : null });
          if (smsGuidance.blocked) {
            this.$toast && this.$toast.error(`${smsGuidance.title}. ${smsGuidance.message}`);
            this.restoreComposerText(content);
            return
          }
        }

        // Send is going through — vac clears its textarea programmatically (no input event fires),
        // so clear the guidance banner to match.
        this.composerText = '';

        // Restore [Form: X] placeholders to HTML <a> anchors for backend form link processing
        let finalContent = content;
        if (finalContent && this.templateLinkMap && Object.keys(this.templateLinkMap).length > 0) {
          for (const [placeholder, htmlAnchor] of Object.entries(this.templateLinkMap)) {
            finalContent = finalContent.replace(placeholder, htmlAnchor);
          }
        }

        // Validate file type and size limits per Twilio Conversations API
        if (file && (file.size || file.type)) {
          const fileSizeMB = file.size / (1024 * 1024);
          const fileName = file.name || 'Unknown file';
          const fileType = (file.type || '').toLowerCase();
          const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
          
          // Define supported file types based on Twilio documentation
          const chatSupportedTypes = [
            // Images
            'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/tiff',
            // Audio
            'audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/amr', 'audio/aac', 'audio/ogg', 'audio/m4a',
            // Video  
            'video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm', 'video/mkv',
            // Documents
            'application/pdf', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            // Archives
            'application/zip', 'application/x-rar-compressed', 'application/x-7z-compressed',
            // Other common types
            'application/json', 'text/csv', 'application/xml'
          ];
          
          // SMS deliverable types (stricter subset)
          const smsDeliverableTypes = ['image/jpeg', 'image/jpg', 'image/png', 'audio/mp3', 'audio/amr', 'video/mp4', 'application/pdf'];
          
          // WhatsApp deliverable types  
          const whatsappDeliverableTypes = ['image/jpeg', 'image/jpg', 'image/png', 'audio/mp3', 'audio/amr', 'video/mp4', 'application/pdf'];
          
          // Check if file type is supported for chat
          const isChatSupported = chatSupportedTypes.some(type => 
            fileType.includes(type) || fileType === type || 
            (type.includes('jpeg') && fileType.includes('jpg')) ||
            (type.includes('jpg') && fileType.includes('jpeg'))
          );
          
          if (!isChatSupported) {
            // Check by file extension as fallback
            const supportedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'mp3', 'wav', 'amr', 'aac', 'ogg', 'm4a', 'mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'pdf', 'txt', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'zip', 'rar', '7z', 'json', 'csv', 'xml'];
            
            if (!supportedExtensions.includes(fileExtension)) {
              this.$toast && this.$toast.error(`File type "${fileType || fileExtension}" is not supported. Please use common file formats like JPG, PNG, PDF, MP4, etc.`);
              return;
            }
          }
          
          // Check maximum limit for chat participants (150MB)
          if (fileSizeMB > 150) {
            this.$toast && this.$toast.error(`File "${fileName}" is too large (${fileSizeMB.toFixed(1)}MB). Maximum file size is 150MB.`);
            return;
          }
          
          // Check SMS delivery compatibility
          const isSMSDeliverable = smsDeliverableTypes.some(type => 
            fileType.includes(type.split('/')[1]) || fileType === type
          );
          
          if (!isSMSDeliverable) {
            this.$toast && this.$toast.info(`File "${fileName}" will be available to chat participants only. SMS participants won't receive this file type (${fileType}).`);
          } else if (fileSizeMB > 5) {
            this.$toast && this.$toast.warning(`File "${fileName}" (${fileSizeMB.toFixed(1)}MB) exceeds 5MB limit for SMS delivery. Chat participants will receive it, but SMS participants won't.`);
          }
          
          // Check WhatsApp delivery compatibility
          const isWhatsAppDeliverable = whatsappDeliverableTypes.some(type => 
            fileType.includes(type.split('/')[1]) || fileType === type
          );
          
          if (!isWhatsAppDeliverable) {
            this.$toast && this.$toast.info(`File "${fileName}" won't be delivered via WhatsApp (unsupported file type: ${fileType}).`);
          } else if (fileSizeMB > 16) {
            this.$toast && this.$toast.warning(`File "${fileName}" (${fileSizeMB.toFixed(1)}MB) exceeds 16MB limit for WhatsApp delivery.`);
          }
        }
        // Optimistic credit draw — drop the header pill the instant THIS user fires a billable
        // send, matching the PatientProfile SMS composer (EnhancedSmsInputBar). By here every send
        // gate has passed (empty / WhatsApp-window-closed / SMS over-limit / cost-cancel /
        // unsupported file), so the message is going out. WhatsApp draws a flat 1 credit (shared SMS
        // pool); SMS draws its segment count from the guidance above; in-house chat draws nothing.
        // The next siteWallet Firestore snapshot reconciles the authoritative number, so a send that
        // fails downstream (opt-out, invalid number) self-heals on the next publish/reload.
        const optimisticCredits = optimisticDrawCredits(replyChannel, smsGuidance);
        if (optimisticCredits > 0) {
          try { this.$store.commit('wallet/applyOptimisticDraw', optimisticCredits) } catch (e) { /* wallet not registered */ }
        }

        const optimisticTs = Date.now()
        const clientNonce = `client-${optimisticTs}-${Math.random().toString(36).slice(2, 10)}`
        // Optimistic UI: add local message immediately (mirrors Chat behavior)
        try {
          const tz = this.allSettings?.GENERAL_TIMEZONE?.PropertyValue || 'America/New_York'
          const now = new Date(optimisticTs)
          const optimisticId = `local-${optimisticTs}`
          const optimisticMsg = {
            _id: optimisticId,
            content: (content && content.trim()) ? content : (file && file.name) || '',
            sender_id: this.effectiveCurrentUserId,
            senderId: this.effectiveCurrentUserId,
            timestamp: new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz }).format(now).replace(',', ''),
            date: new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: tz }).format(now),
            saved: true,
            distributed: false
          }
          if (file) {
            optimisticMsg.file = {
              name: file.name,
              size: file.size,
              type: file.type,
              url: file.localUrl || file.url || ''
            }
          }
          optimisticMsg.tsNum = optimisticTs
          optimisticMsg.channel = replyChannel // per-message badge reflects the chosen channel immediately
          optimisticMsg.attributes = {
            outbound: true,
            optimistic: true,
            clientNonce,
            channel: replyChannel
          }
          const newMsgs = (this.messages || []).concat([optimisticMsg])
          this.$store.commit('conversationsV2/setMessages', newMsgs)
          this.$store.commit('conversationsV2/setMessagesLoaded', true)
        } catch(_) {}
        // Update rooms list preview immediately
        try { this.$store.dispatch('conversationsV2/updateRoomLastMessageLocally', { roomId, content, ts: Date.now() }); } catch(_) {}
        // Backend expects: { roomId, message: { content, author?, attributes? }, backgroundTaskPayload: { siteID } }
        const payload = {
          roomId,
          message: {
            content: finalContent,
            author: this.user && (this.user.Name || this.user.FullName || this.user.Username) || 'TeamIO',
            // channel tells the backend which medium to send on (explicit, not the sticky thread channel)
            attributes: { outbound: true, clientNonce, channel: replyChannel }
          },
          backgroundTaskPayload: {
            siteID: this.siteID,
            smsText: finalContent,
            employeeName: this.user?.Name || ''
          }
        }
        // Optional media support (vue-advanced-chat passes single file param)
        if (file) {
          payload.message.file = {
            name: file.name,
            size: file.size,
            type: file.type,
            url: file.localUrl || file.url || ''
          }
          if (file.audio) {
            payload.message.file.audio = true
            payload.message.file.duration = file.duration
          }
          try {
            // Provide base64 for any file type if we only have a blob/local URL so backend can upload to Twilio MCS
            let sourceBlob = file.blob || null
            if (!sourceBlob && payload.message.file.url && /^blob:\/\//.test(payload.message.file.url)) {
              const resp = await fetch(payload.message.file.url)
              sourceBlob = await resp.blob()
            }
            if (sourceBlob) {
              const dataUrl = await this.blobToDataUrl(sourceBlob, file.type)
              if (dataUrl) {
                // dataUrl format: data:<mime>;base64,<...>
                payload.message.file.base64 = dataUrl
              }
            }
          } catch (e) {
            // Failed to prepare file base64
          }
        }
        // Optional reply support
        if (replyMessage) {
          payload.message.replyMessage = {
            _id: replyMessage._id,
            content: replyMessage.content,
            senderId: replyMessage.senderId
          }
          if (replyMessage.file) {
            payload.message.replyMessage.file = replyMessage.file
          }
        }
        const resp = await axios.post('/send-message', payload)
        const data = resp && resp.data
        // Expected "not sent" outcomes (opt-out / invalid or undeliverable number) now
        // come back as HTTP 200 { success:false, error } so the backend doesn't page.
        // Surface them (warning for a known non-delivery, error otherwise) and KEEP the
        // draft + template state — do not clear as if the message was sent.
        if (data && data.success === false) {
          // A PATIENT_OPTED_OUT refusal surfaces the SERVER's reason (or GENERIC_OPT_OUT)
          // via the shared helper — not the raw error code — through the SAME toast.
          const optOut = optOutResultFromSendFailure(data)
          const { message, isNonDelivery } = classifyNonDelivery(data)
          const finalMessage = optOut ? optOut.reason : message
          if (optOut || isNonDelivery) this.$toast && this.$toast.warning(finalMessage, { duration: 8000 })
          else this.$toast && this.$toast.error(finalMessage)
          return
        }
        // Clear template link state after successful send
        this.templateLinkMap = {};
        this.templateWikiPageID = null;
        // Do not refetch messages here; live listener will update to avoid flicker
      } catch (e) {
        // Network / 5xx / genuine faults — and, during rollout, the LEGACY shape where an
        // older backend still returns 403/400 for opt-out/invalid (axios rejects). Handle
        // both via the same classification.
        console.error('[ConversationsV2] onSendMessage error', e)
        const errData = e && e.response && e.response.data
        const optOut = optOutResultFromSendFailure(errData)
        const { message, isNonDelivery } = classifyNonDelivery(errData)
        const finalMessage = optOut ? optOut.reason : message
        if (optOut || isNonDelivery) {
          // Warning style — these require action in Open Dental, not a retry.
          this.$toast && this.$toast.warning(finalMessage, { duration: 8000 })
        } else {
          this.$toast && this.$toast.error(finalMessage)
        }
      }
    },
    // ===== APPROVED MESSAGES (step 4 part 2) =====
    // Loaded the first time a closed window is on screen, once per site. Any failure (no session, offline, a
    // 5xx) leaves the list empty, so the button simply is not shown; the banner then says only that the window
    // has closed. It never blocks anything else on the page.
    async loadApprovedMessages() {
      const siteID = this.siteID
      if (!siteID || this.approvedLoadedFor === siteID) return
      this.approvedLoadedFor = siteID
      // A re-ask while a list is already on screen keeps showing it (no flash back to "one moment").
      if (this.approvedStatus !== 'loaded') this.approvedStatus = 'loading'
      try {
        const list = await fetchApprovedWhatsAppMessages(siteID, this.$store.state.getAuth())
        if (this.siteID === siteID) { this.approvedMessages = list; this.approvedStatus = 'loaded' }
      } catch (e) {
        console.warn('[ConversationsV2] approved messages not loaded:', e && e.message)
        // Allow another try the next time a closed window shows (a blip should not hide the button for the session).
        if (this.approvedLoadedFor === siteID) this.approvedLoadedFor = ''
        // 'failed' makes New Conversation show the form: a blip must not hide it from an office that has messages.
        if (this.approvedStatus !== 'loaded') this.approvedStatus = 'failed'
      }
    },
    // Put the closed-window panel INSIDE the chat's footer (the message-box area). Idempotent; retries because the
    // library renders the footer asynchronously after a room opens, and re-run when anything may have re-rendered it.
    placeClosedPanel(attempt = 0) {
      const panel = this.$el && this.$el.querySelector('.wa-closed-panel')
      if (!panel) return
      const footer = this.$el.querySelector('.vac-room-footer')
      if (!footer) { if (attempt < 10) setTimeout(() => this.placeClosedPanel(attempt + 1), 200); return }
      if (panel.parentElement !== footer) footer.appendChild(panel)
    },
    // Send one approved message to the open conversation. The body carries only the KEY (the server builds the
    // text and refuses anything that is not an approved message of this site), and every refusal comes back as a
    // plain sentence we show as it is. The conversation updates through the live listener, like any other send.
    async sendApproved(msg) {
      if (this.approvedSending || !msg || !this.selectedRoomID) return
      this.approvedSending = true
      try {
        const payload = buildApprovedSendPayload({
          roomId: this.selectedRoomID,
          key: msg.key,
          siteID: this.siteID,
          authorName: this.user && (this.user.Name || this.user.FullName || this.user.Username),
          employeeName: this.user && this.user.Name,
          clientNonce: `appr_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
        })
        const resp = await axios.post('/send-message', payload)
        const data = resp && resp.data
        if (data && data.success === false) {
          // Same quiet 200 the normal send uses for an opted-out patient or a number that cannot be reached.
          const optOut = optOutResultFromSendFailure(data)
          const { message, isNonDelivery } = classifyNonDelivery(data)
          const text = optOut ? optOut.reason : message
          if (optOut || isNonDelivery) this.$toast && this.$toast.warning(text, { duration: 8000 })
          else this.$toast && this.$toast.error(text)
          return
        }
        this.$toast && this.$toast.success(APPROVED_SENT_TOAST)
        this.approvedPick = ''
      } catch (e) {
        console.error('[ConversationsV2] sendApproved error', e)
        const errData = e && e.response && e.response.data
        const optOut = optOutResultFromSendFailure(errData)
        if (optOut) this.$toast && this.$toast.warning(optOut.reason, { duration: 8000 })
        else this.$toast && this.$toast.error(approvedSendFailureMessage(errData))
      } finally {
        this.approvedSending = false
      }
    },
    blobToDataUrl(blob, mimeType) {
      return new Promise((resolve) => {
        try {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = () => resolve('')
          if (mimeType && blob && blob.type !== mimeType) {
            try {
              blob = new Blob([blob], { type: mimeType })
            } catch (_) {}
          }
          reader.readAsDataURL(blob)
        } catch (_) {
          resolve('')
        }
      })
    },
    addRoom() {
      this.resetForms()
      this.addNewRoom = true
      // A WhatsApp-only office sees the explainer card when it has no approved message, so ask. Always re-ask
      // (clearing the "already loaded" mark): a message approved while this page stayed open must show up.
      if (this.smsOutboundOff) {
        this.approvedLoadedFor = ''
        this.loadApprovedMessages()
      }
    },
    openConversationsSettings() {
      const handle = this.$showPanel({
        component: ConversationsSettings,
        openOn: 'right',
        width: 720,
        disableBgClick: true,
        disableEscClick: true,
      })
      handle.promise.then(() => { /* panel closed */ })
    },
    /**
     * Open the settings panel when arriving via FeatureNudge CTA
     * (e.g. /app/conversations?openSettings=photo-imaging).
     * Clears the query param afterward so refresh/back-button doesn't re-open.
     */
    maybeAutoOpenSettings() {
      try {
        if (this.$route && this.$route.query && this.$route.query.openSettings === 'photo-imaging') {
          this.openConversationsSettings()
          // Strip the param so refresh/back doesn't keep re-opening it
          const newQuery = { ...this.$route.query }
          delete newQuery.openSettings
          this.$router.replace({ query: newQuery }).catch(() => {})
        }
      } catch (e) { /* non-fatal */ }
    },
    /**
     * /app/conversations?roomId=<id> opens that thread. Waits for the room list (re-run by the
     * roomsLoaded watcher), then selects the room — or, for a thread outside the loaded window, fetches
     * it as a local room (conversationsV2/fetchRoomById). The query param is left in place on purpose:
     * App.vue keys <router-view> by fullPath, so replacing it would remount this page and drop the
     * selection. Runs once per page instance.
     */
    async maybeOpenRoomFromQuery() {
      const roomId = readRoomIdQuery(this.$route && this.$route.query)
      if (!roomId || this.deepLinkRoomHandled === roomId || !this.roomsLoaded || !this.siteID) return
      this.deepLinkRoomHandled = roomId
      try {
        const loaded = (this.rooms || []).find(r => r && r.roomId === roomId)
        const room = loaded || await this.$store.dispatch('conversationsV2/fetchRoomById', { roomId, siteID: this.siteID })
        if (room) {
          this.onRoomSelected(room)
        } else {
          this.$toast && this.$toast.warning('That conversation is no longer available.')
        }
      } catch (e) {
        console.error('[ConversationsV2] could not open linked conversation', e)
        this.$toast && this.$toast.error("Couldn't open that conversation — find it in the list instead.")
      }
    },
    patientCustomLabel({ FName, LName, Birthdate }) {
      // Birthdate arrives as 'YYYY-MM-DD' (runQuery dateStrings:true). Never `new Date()` it:
      // a date-only ISO string parses as UTC midnight and local getters show the previous day
      // in every UTC-negative zone (Harrisburg Smiles, 2026-08-28). formatDate() = moment(local)
      // + the site's DateFormat, same as the header search.
      const dobStr = this.formatDate(Birthdate) || 'N/A';
      return `${FName} ${LName} (DOB:${dobStr})`;
    },
    // Each control clears the other, so the form can only ever show one recipient.
    onTypedPhoneInput() {
      if (this.typedPhone && this.selectedUsers && this.selectedUsers.length) this.selectedUsers = []
    },
    onPatientSelected(value) {
      if (value) this.typedPhone = ''
    },
    resetForms() {
      this.disableForm = false
      this.creatingConversation = false
      this.selectedUsers = []
      this.typedPhone = ''
    },
    async createRoom() {
      // Re-entrancy guard: a second call while one is in flight (a double-click that beats the
      // button's :disabled toggle) must bail before it can POST /create-conversation twice.
      if (this.creatingConversation || this.disableForm) return
      if (!this.allSettings?.GENERAL_PHONE?.PropertyValue) {
        this.$toast && this.$toast.error('Site SMS phone (GENERAL_PHONE) is not set. Please configure it in Settings.')
        return
      }
      // THE chokepoint. Every route to POST /create-conversation runs through this one call, so a
      // refused verdict and "no message was sent" are the same fact. Pinned by
      // cypress/node-tests/non-patient-conversation-entry.test.js.
      const target = resolveNewConversationTarget({
        selectedPatient: this.selectedUsers,
        typedPhone: this.typedPhone,
        roster: this.patientsToChatWith,
      })
      if (!target.ok) {
        this.$toast && this.$toast.error(target.message, { duration: 8000 })
        return
      }
      const patient = target.patient
      let phone
      if (target.kind === 'PATIENT' && !target.matchedFromTypedPhone) {
        // Existing path, unchanged: the number comes off the Open Dental record, so a bad one is a
        // data problem the receptionist fixes in OD.
        phone = String(patient.WirelessPhone || '').replace(/[()\-\s]/g, '')
        if (!phone || /\D/.test(phone)) {
          this.$toast && this.$toast.error('Selected patient has an invalid WirelessPhone in Open Dental')
          return
        }
      } else {
        // Typed number, already normalized to the same E.164 string the engine derives when it
        // routes the send and the reply — that agreement is what keeps both halves of the
        // conversation in ONE thread (typed-phone-entry.test.js section 1).
        phone = target.e164
      }
      if (target.matchedFromTypedPhone) {
        // The number they typed turns out to be a patient. Say so: they are about to open a chart
        // conversation, not the number-only thread the box implies.
        const matchedName = patient.Patient || [patient.FName, patient.LName].filter(Boolean).join(' ') || 'a patient'
        this.$toast && this.$toast.info(`That number belongs to ${matchedName} — opening their conversation.`)
      }
      // Lock the form NOW — BEFORE the async opt-out lookup below — so the submit button
      // (:disabled="disableForm || creatingConversation") can't be clicked again and
      // double-POST /create-conversation while the lookup is in flight. The finally at the
      // end resets both flags on EVERY path (opt-out blocked, success, failure).
      this.disableForm = true
      this.creatingConversation = true
      try {
        // Opt-out gate (Task D3). The backend refuses opted-out sends regardless; this stops
        // us from opening a dead thread the practice can never text into.
        const optOutTz = this.allSettings?.GENERAL_TIMEZONE?.PropertyValue || ''
        // 1) LOCAL rule (texted STOP / OD Text-OK = No) — cheap, no round-trip. It reads OD flags
        //    off the patient record, so there is nothing to check for a number with no patient;
        //    the server ledger below is phone-scoped and still catches a STOP from that number.
        if (patient) {
          const localBlock = getTextBlockReason({ patient, timezone: optOutTz })
          if (localBlock.blocked) {
            this.$toast && this.$toast.warning(localBlock.message, { duration: 8000 })
            return
          }
        }
        // 2) SERVER/HQ opt-out ledger. fetchSmsOptOutStatus never throws (returns [] on error);
        //    an empty result PROCEEDS — a transient lookup blip must not wall off texting, and
        //    the backend still refuses the actual send.
        try {
          const optOutItem = patient ? { phone, patNum: patient.PatNum } : { phone }
          const optOutResults = await this.fetchSmsOptOutStatus({ siteID: this.siteID, items: [optOutItem] })
          const blockedResult = (optOutResults || []).find(r => r && r.blocked)
          if (blockedResult) {
            const serverReason = getTextBlockReason({
              patient, timezone: optOutTz,
              optOutResult: { blocked: true, code: blockedResult.code, reason: blockedResult.reason },
            }).message
            this.$toast && this.$toast.warning(serverReason, { duration: 8000 })
            return
          }
        } catch (_) { /* lookup failure must not block; the backend still refuses the send */ }
        const payload = {
          SiteID: this.siteID,
          Employee: this.user,
          // PATIENT -> the whole patient row, as before. PHONE -> { WirelessPhone } and nothing
          // else: createConversationInhouse writes patNum / patientName / friendlyName only `if
          // present`, and the thread self-heals into a real patient thread the moment that number
          // is added to Open Dental.
          Users: newConversationUsersPayload(target),
          SitePhone: this.allSettings.GENERAL_PHONE.PropertyValue,
          SiteName: this.user?.SiteName
        }
        const response = await axios.post('/create-conversation', payload)
        const result = response && response.data ? response.data : {}
        // Optimistically add a local room so it appears immediately until Firestore snapshot catches up
        try {
          const patientPhone = (patient && patient.WirelessPhone) ? String(patient.WirelessPhone).replace(/[()\-\s]/g, '') : phone
          // A thread with no patient is labeled by its own number — never a placeholder name.
          // "Unknown Patient" in particular is what cleanupUnknownPatients matches on.
          const phoneLabel = formatPhoneForDisplay(phone) || phone
          const displayName = (patient && patient.Patient) || [patient?.FName, patient?.LName].filter(Boolean).join(' ') || phoneLabel
          const localRoom = {
            // In-house createConversation returns conversationSID (conv_…); keep legacy sid as a fallback.
            roomId: result?.conversationSID || result?.sid || 'local-'+Date.now(),
            sid: result?.conversationSID || result?.sid,
            friendlyName: result?.friendly_name || (patient && patient.Patient) || phoneLabel,
            roomName: result?.friendly_name || (patient && patient.Patient) || phoneLabel,
            siteID: this.siteID,
            dateUpdated: new Date(),
            users: [
              { _id: 'site', username: this.user?.SiteName || 'Site' },
              { _id: patientPhone, username: displayName }
            ],
            lastMessage: { content: '', timestampNum: Date.now(), date: Date.now(), timestamp: new Date().toLocaleString() }
          }
          this.$store.dispatch('conversationsV2/addLocalRoom', localRoom)
          this.suppressAutoFetchUntil = Date.now() + 1500
          // After suppression window, explicitly set selected room to the new room to keep selection stable
          setTimeout(() => { this.selectedRoomID = localRoom.roomId }, 1600)
          // Do NOT set room-id immediately; let two-panel render first like Chat
          // this.selectedRoomID = localRoom.roomId
        } catch(_) {}
        this.addNewRoom = false
        this.selectedUsers = []
        this.typedPhone = ''
        this.$toast && this.$toast.success(`Conversation started with ${patient?.Patient || formatPhoneForDisplay(phone) || phone}`)
      } catch (e) {
        console.error('[ConversationsV2] createRoom error', e)
        this.$toast && this.$toast.error('Failed to create conversation')
      } finally {
        this.disableForm = false
        this.creatingConversation = false
      }
    }
  },
  watch: {
    // The closed-window banner appears above the chat (and goes away again), moving where the chat starts:
    // refit it to the viewport right away instead of leaving its bottom off screen until a resize.
    composerBlocked(blocked) {
      if (blocked) this.loadApprovedMessages()
      else this.approvedPick = ''
      this.$nextTick(() => { this.placeClosedPanel(); this.fitChatToViewport() })
    },
    rooms() {
      // Keep the WhatsApp room-row icons in sync as the room set updates (new threads,
      // channel changes). Cheap: regenerates one stylesheet keyed by WhatsApp room ids.
      this.$nextTick(() => this.applyWhatsAppRoomStyles());
      // Same for the "Not in Open Dental" pill: a thread that self-heals must lose it here.
      this.$nextTick(() => this.applyNotInOpenDentalRoomStyles());
    },
    settings(newVal, oldVal) {
      // Raw settings array from mixin
      if (newVal !== oldVal) {
        this.logSettingsSnapshot('watch: settings changed')
      }
    },
    allSettings(newVal, oldVal) {
      if (newVal && !oldVal && !this.initialized) {
        this.logSettingsSnapshot('watch: allSettings available')
        this.initV2()
      }
    },
    selectedRoomID() {
      // The picker belongs to ONE thread: forget a half-made pick when another one opens.
      this.approvedPick = '';
      // The panel is node-moved into the chat's footer: put it there again for this thread.
      this.$nextTick(() => this.placeClosedPanel());
      // Clear template link state when switching rooms
      this.templateLinkMap = {};
      this.templateWikiPageID = null;
      // Reset the explicit reply-channel choice so each thread starts on its smart default.
      this.replyChannelOverride = null;
      // Render recent bar when room changes (needs nextTick for footer to exist)
      this.$nextTick(() => this.renderRecentBar());
    },
    // Trigger auto-open when the query param is added while already on this page
    // (e.g. user clicks a FeatureNudge CTA without leaving conversations).
    '$route.query.openSettings'(newVal) {
      if (newVal === 'photo-imaging') {
        this.maybeAutoOpenSettings()
      }
    },
    // A ?roomId= deep link can arrive before the rooms listener's first snapshot.
    roomsLoaded(loaded) {
      if (loaded) this.maybeOpenRoomFromQuery()
    }
  }
}
</script>

<style lang="scss" scoped>
// Shared design-system tokens (Nilay's redesign): the WhatsApp-only New Conversation card is built from these.
@import '../../styles/design-tokens';

$brand-green: #3abf94;

// =====================================================
// + INSERT BUTTON (lives in the Quick Actions bar — see renderRecentBar)
// =====================================================
.conversations-page {
  position: relative;
}

.insert-button-wrapper {
  display: inline-flex;
  flex-shrink: 0;

  // Compact button to fit the Quick Actions row alongside chips
  ::v-deep .insert-dropdown > .btn {
    padding: 2px 10px;
    font-size: 11px;
    line-height: 1.5;
    border-radius: 12px;
  }
}

.emoji-inline-grid {
  max-height: 200px;
  overflow-y: auto;
  max-width: 260px;
}

.emoji-category-title {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  margin: 6px 0 3px;
}

.emoji-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
}

.emoji-item {
  font-size: 20px;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background 0.15s;

  &:hover {
    background: #f0f0f0;
  }
}

// =====================================================
// GROUP MMS WARNING BANNER
// Shows when staff is viewing a Group MMS conversation
// =====================================================
.group-mms-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  border: 1px solid #fed7aa;
  border-radius: 8px;
  border-left: 4px solid #f97316;

  > i {
    font-size: 1.25rem;
    color: #ea580c;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .warning-content {
    flex: 1;

    strong {
      display: block;
      font-size: 0.9375rem;
      font-weight: 600;
      color: #9a3412;
      margin-bottom: 4px;
    }

    p {
      font-size: 0.8125rem;
      color: #c2410c;
      margin: 0;
      line-height: 1.4;
    }
  }
}

// =====================================================
// "NOT IN OPEN DENTAL" BANNER
// Quieter than the group-MMS warning on purpose: this is information, not a risk. It gates
// nothing, and the thread is perfectly fine to reply to — it just isn't written to a chart.
// =====================================================
.not-in-od-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 12px;
  background: #f4f6f9;
  border: 1px solid #dfe5ec;
  border-left: 3px solid #8a9bb0;
  border-radius: 6px;
  color: #4a5b6e;
  font-size: 13px;
  line-height: 1.4;

  > i {
    color: #6b7d92;
    flex-shrink: 0;
  }
}

// Live SMS-guidance bar. placeGuidanceBar() relocates it into .vac-room-footer, right under the
// composer box, so it lines up with the composer (like the patient SMS bar). Sits on the footer's
// own background; capped height with internal scroll so a long block banner can't grow unbounded.
// Hidden by default and shown via the reactive .is-visible class (an in-place patch) so the
// relocated node is never destroyed/recreated while typing — see the template comment.
.conv-sms-guidance-bar {
  display: none;
}
.conv-sms-guidance-bar.is-visible {
  display: block;
  margin-top: 8px;
  max-height: 132px;
  overflow-y: auto;
}

// Reply-channel selector bar (shown above the chat when the site offers WhatsApp).
.reply-channel-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 8px 12px;
  margin-bottom: 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;

  .rcb-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
  }

  .rcb-toggle {
    display: inline-flex;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    overflow: hidden;
  }

  .rcb-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    font-size: 0.8125rem;
    font-weight: 500;
    background: #fff;
    color: #475569;
    border: none;
    cursor: pointer;

    & + .rcb-btn { border-left: 1px solid #cbd5e1; }

    &.active { background: #0ea5e9; color: #fff; }
    &.rcb-btn--wa.active { background: #25d366; }
  }

  .rcb-window {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    color: #047857;

    &.closed { color: #c2410c; }
  }
}

// CLOSED 24-HOUR WINDOW + APPROVED MESSAGES (2026-10-14, replaces the 10-11 banner and floating list): while the
// window is closed the text box is replaced, in place, by ONE quiet panel inside the chat's message-box area
// (placeClosedPanel moves it into .vac-room-footer). Built from the shared design tokens; the single accent is teal.
// Every other child of the footer (text box, quick actions, + Insert, SMS guidance) is hidden, never destroyed, so
// everything the page relocates there survives, and a hidden box cannot be focused or typed into. They come back by
// themselves when the window reopens. The panel's own rules sit at the top level of this scoped block: the node is
// moved into the library's DOM but still carries this component's scope attribute (same as .conv-sms-guidance-bar).
.wa-closed-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px 16px;
  background: $neutral-0;
  border-top: 1px solid $neutral-200;
  color: $neutral-950;

  .wcp-status {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .wcp-dot {
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    margin-top: 7px;
    border-radius: $radius-pill;
    background: $amber-500;
  }

  .wcp-text {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
    color: $neutral-700;
  }

  // The title carries no full stop (the toast builds "title. body"), so it sits on its own line.
  .wa-wb-title { display: block; font-weight: 700; color: $neutral-950; }

  .wcp-compose {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .wcp-choose {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .wcp-label {
    flex: 0 0 auto;
    margin: 0;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $neutral-500;
  }

  .wcp-select {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 40px;
    padding: 0 12px;
    border: 1px solid $neutral-200;
    border-radius: $radius-btn;
    background: $neutral-0;
    color: $neutral-950;
    font-size: 0.875rem;
    font-weight: 600;

    &:focus-visible { outline: 2px solid $teal-600; outline-offset: 1px; }
    &:disabled { opacity: 0.6; }
  }

  // What the patient will get, drawn as a message bubble. Capped to the window so a long message scrolls inside the
  // panel and Send is always reachable.
  .wcp-preview {
    padding: 10px 14px;
    border-radius: $radius-tile;
    background: $teal-50;
    max-height: min(160px, 24vh);
    overflow-y: auto;
  }

  .wcp-preview-title {
    margin: 0 0 2px;
    font-size: 0.8rem;
    font-weight: 700;
    color: $teal-700;
  }

  .wcp-preview-text {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
    color: $neutral-950;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .wcp-preview-caption {
    margin: 4px 0 0;
    font-size: 0.75rem;
    color: $neutral-500;
  }

  .wcp-actions {
    display: flex;
    justify-content: flex-end;
  }

  // No approved message yet: one short explanation and the way to get one. A soft box, no border (the card's look).
  .wcp-none {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 16px;
    padding: 12px 14px;
    border-radius: $radius-tile;
    background: $neutral-50;
  }

  .wcp-none-body {
    flex: 1 1 320px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .wcp-none-text,
  .wcp-none-note {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
    color: $neutral-700;
  }

  // Said plainly, set apart a little: an approved reminder is not a message that can start a conversation.
  .wcp-none-note {
    padding-top: 8px;
    border-top: 1px solid $neutral-200;

    strong { color: $neutral-950; }
  }

  // Drawn arrow and spinner: no icon font inside the chat area (see the template comment).
  .wcp-arrow { flex: 0 0 auto; }

  .wcp-spinner {
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #fff;
    border-radius: 50%;
    animation: wcp-spin 0.8s linear infinite;
  }

  .wcp-none-ask {
    flex: 1 1 100%;
    margin: 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: $neutral-950;
  }

  // The link is drawn exactly like the Send button (one primary look in the panel).
  .wcp-send,
  .wcp-none-cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 40px;
    padding: 0 22px;
    border: 1px solid $teal-700;
    border-radius: $radius-btn;
    background: $teal-700;
    color: #fff;
    font-size: 0.875rem;
    font-weight: 600;
    box-shadow: $shadow-primary;
    cursor: pointer;
    transition: background-color 0.15s ease, box-shadow 0.15s ease;

    &:hover:not(:disabled) { background: $teal-800; border-color: $teal-800; box-shadow: $shadow-primary-hov; color: #fff; text-decoration: none; }
    &:focus-visible { outline: 2px solid $teal-600; outline-offset: 2px; }
    &:disabled { opacity: 0.6; cursor: default; box-shadow: none; }
  }

  .wcp-none-cta { flex: 0 0 auto; text-decoration: none; }
}

@keyframes wcp-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 600px) {
  .wa-closed-panel {
    padding: 12px;

    .wcp-choose { flex-direction: column; align-items: stretch; gap: 4px; }
    .wcp-send { width: 100%; }
    .wcp-none-cta { width: 100%; }
  }
}

// While the window is closed the footer holds ONLY the panel. :deep() wraps the whole selector so no scope attribute
// is appended to :not(...) (the library's own nodes do not carry one).
.wa-composer-blocked :deep(.vac-room-footer > :not(.wa-closed-panel)) {
  display: none !important;
}


// =====================================================
// CONVERSATIONS PAGE HEADER - Modern styling matching Chat page
// =====================================================
.conversations-page {
  position: relative;
}

.conversations-page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;

  .header-icon-wrapper {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: linear-gradient(135deg, $brand-green 0%, darken($brand-green, 10%) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 14px rgba(58, 191, 148, 0.3);
    flex-shrink: 0;

    i {
      font-size: 1.5rem;
      color: #fff;
    }
  }

  .header-titles {
    flex: 1;

    h1 {
      font-size: 1.625rem;
      font-weight: 700;
      color: #1e293b;
      margin: 0 0 4px 0;
      line-height: 1.2;
    }

    p {
      font-size: 0.875rem;
      color: #64748b;
      margin: 0;
    }

    .sms-number-pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin-top: 6px;
      padding: 4px 12px;
      background: #f0fdf4;
      border: 1px solid #bbf7d0;
      border-radius: 20px;
      font-size: 0.78rem;
      color: #15803d;
      cursor: default;

      i {
        font-size: 0.7rem;
      }

      strong {
        letter-spacing: 0.3px;
      }
    }

    // Photo-cap note. Deliberately quieter than the SMS-number pill — it is reference
    // information, not a status. Wraps rather than truncating on narrow screens.
    .photo-note {
      display: flex;
      align-items: baseline;
      gap: 6px;
      margin-top: 6px;
      font-size: 0.75rem;
      line-height: 1.35;
      color: #64748b;

      i {
        font-size: 0.7rem;
        flex-shrink: 0;
        position: relative;
        top: 1px;
      }
    }
  }

  .new-conversation-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: $brand-green;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(58, 191, 148, 0.25);

    i {
      font-size: 0.875rem;
    }

    &:hover:not(:disabled) {
      background: darken($brand-green, 6%);
      box-shadow: 0 4px 12px rgba(58, 191, 148, 0.35);
      transform: translateY(-1px);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  // Phones: the one-row header squeezed the titles into a narrow column and pushed "New Conversation"
  // off the right edge (119px of sideways page scroll at 390px). Keep icon + titles on the first row and
  // move the buttons to a full-width row below.
  @media (max-width: 640px) {
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
    padding-bottom: 16px;

    // The icon is decorative, and on a phone it cost 56px of a 288px content column AND indented
    // every line of the title block to x=123 while the buttons below started at x=67 — the header
    // read as two different left margins stacked on each other (measured at 390px, 2026-09-18).
    // Dropping it lets the heading, the subtitle, the SMS-number pill, the photo note and the
    // button row all share one left edge.
    .header-icon-wrapper {
      display: none;
    }

    .header-titles {
      flex: 1 1 100%;
      min-width: 0;

      h1 {
        font-size: 1.375rem;
      }

      // These are rem-based, and the app root is 14px — so on a phone they painted at 10.5px and
      // 10.92px, below the 12px floor the rest of this page uses. Written in px for the same reason
      // the phone input is: a rem here is 14px, not 16px.
      p { font-size: 12px; }
      .photo-note { font-size: 12px; }
      .sms-number-pill {
        font-size: 12px;
        i { font-size: 11px; }
      }

      .sms-number-pill {
        display: flex;
        flex-wrap: wrap;
        column-gap: 4px;
        row-gap: 0;
        width: fit-content;
        max-width: 100%;
        border-radius: 12px;

        strong {
          white-space: nowrap;
        }
      }
    }

    .new-conversation-btn {
      // basis 0 so the settings gear stays on the same row even at 320px
      flex: 1 1 0;
      min-width: 0;
      justify-content: center;
      padding: 10px 12px;
      white-space: nowrap;
    }

    // `&` doubles the specificity on purpose. The base .conv-settings-btn rule is declared AFTER
    // this media query in the file, so at equal specificity its margin-left: 8px / margin-right: 4px
    // won and this block did nothing — the gear sat 20px off the New Conversation button and 4px
    // short of the card's right edge, which is what made the button row look ragged at 390px.
    // Doubling the class beats source order without moving a 180-line block around.
    &.conversations-page-header .conv-settings-btn {
      margin-left: 0;
      margin-right: 0;
    }
  }

  .conv-settings-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    margin-left: 8px;
    margin-right: 4px;
    padding: 0;
    border-radius: 8px;
    background: white;
    border: 1px solid #e2e8f0;
    color: #718096;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;

    &:hover {
      background: #f7fafc;
      color: $brand-green;
      border-color: $brand-green;
    }

    .fa {
      font-size: 14px;
      transition: transform 0.3s ease;
    }

    &:hover .fa {
      transform: rotate(45deg);
    }
  }
}

// =====================================================
// NEW CONVERSATION at a WhatsApp-only office (2026-10-14)
// The explainer card (no approved message yet) and the one-line callout on the form (has one).
// Lives OUTSIDE .chat-forms on purpose: that block's `*` and `button` resets would restyle the card.
// WhatsApp's own green (#25d366) is hardcoded because it identifies the channel (same as WhatsAppTab).
// =====================================================
.wa-start-card {
  margin-bottom: 16px;
  padding: 24px;
  background: $neutral-0;
  border: 1px solid $neutral-200;
  border-radius: $radius-card;
  box-shadow: $shadow-sm;
  color: $neutral-950;

  &--loading {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 18px 24px;
    color: $neutral-500;
    font-size: 0.9rem;
  }

  &__head {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  &__icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: $radius-tile;
    background: #25d366;
    color: #fff;
    font-size: 1.6rem;
    line-height: 1;
  }

  &__intro {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 720px;
  }

  &__title {
    margin: 0 0 6px;
    font-size: 1.15rem;
    font-weight: 700;
    color: $neutral-950;
  }

  &__text {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.55;
    color: $neutral-700;
  }

  // The one thing people get wrong: an approved reminder is not a message staff can start a conversation with. Amber =
  // "read this"; an inset bar instead of a border (the card sits beside .chat-forms rules that reset borders).
  &__note {
    margin: 16px 0 0;
    padding: 12px 14px 12px 16px;
    border-radius: $radius-tile;
    background: $amber-50;
    box-shadow: inset 3px 0 0 $amber-500;
    font-size: 0.875rem;
    line-height: 1.5;
    color: $neutral-700;

    strong { color: $neutral-950; }
  }

  &__steps {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin: 20px 0 0;
    padding: 0;
    list-style: none;
  }

  &__step {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px;
    background: $neutral-50;
    border: 1px solid $neutral-100;
    border-radius: $radius-tile;
  }

  &__num {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: $radius-pill;
    background: $teal-50;
    color: $teal-700;
    font-size: 0.8rem;
    font-weight: 700;
  }

  &__step-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    font-size: 0.85rem;
    line-height: 1.45;
    color: $neutral-700;

    strong {
      font-size: 0.9rem;
      font-weight: 700;
      color: $neutral-950;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 20px;
  }

  &__cta,
  &__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 42px;
    padding: 0 20px;
    border-radius: $radius-btn;
    font-size: 0.875rem;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

    &:focus-visible { outline: 2px solid $teal-600; outline-offset: 2px; }
  }

  &__cta {
    border: 1px solid $teal-700;
    background: $teal-700;
    color: #fff;
    box-shadow: $shadow-primary;

    &:hover {
      background: $teal-800;
      border-color: $teal-800;
      color: #fff;
      text-decoration: none;
      box-shadow: $shadow-primary-hov;
    }
  }

  &__close {
    border: 1px solid $neutral-200;
    background: $neutral-0;
    color: $neutral-700;

    &:hover { background: $neutral-50; border-color: $neutral-500; }
  }

  &__ask {
    flex: 1 1 260px;
    margin: 0;
    padding: 10px 14px;
    background: $amber-50;
    border: 1px solid $amber-200;
    border-radius: $radius-btn;
    font-size: 0.875rem;
    color: $amber-700;
  }

  &__fine {
    margin: 14px 0 0;
    font-size: 0.8rem;
    color: $neutral-500;
  }
}

// At tablet width the three steps stack; the card keeps the same padding rhythm on a phone.
@media (max-width: 768px) {
  .wa-start-card {
    padding: 18px 16px;

    &__steps { grid-template-columns: 1fr; }
    &__head { gap: 12px; }
    &__cta,
    &__close { flex: 1 1 100%; }
  }
}

// One visible line on top of the form (the office HAS an approved message). A soft teal panel with an accent bar
// drawn as an inset shadow: .chat-forms resets border-top / border-bottom-color on every child.
.chat-forms .wa-form-callout {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  padding: 12px 14px;
  background: $teal-50;
  border: 0;
  border-radius: $radius-btn;
  box-shadow: inset 3px 0 0 #25d366;
  font-size: 0.875rem;
  line-height: 1.5;
  color: $neutral-700;

  .fa { flex: 0 0 auto; margin-top: 3px; font-size: 1rem; color: #25d366; }
}

// =====================================================
// CHAT FORMS - Modern styling matching Chat page
// =====================================================
.chat-forms {
  padding: 20px 24px;
  background: #f8fafc;
  border-radius: 12px;
  margin-bottom: 16px;
  border: 1px solid #e2e8f0;

  // AGGRESSIVE: Remove all possible borders and gray bars from all elements
  *,
  *::before,
  *::after {
    border-top: none !important;
    border-bottom-color: transparent !important;
  }

  // Re-apply specific borders we want
  border: 1px solid #e2e8f0;

  form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 !important;
    margin: 0 !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }

  h3 {
    font-size: 1.0625rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 4px 0;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  .form-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
  }

  button {
    background: $brand-green;
    color: #fff;
    outline: none;
    cursor: pointer;
    border-radius: 8px;
    padding: 10px 20px;
    border: none !important;
    font-size: 0.875rem;
    font-weight: 600;
    transition: all 0.2s ease;
    height: 42px;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover:not(:disabled) {
      background: darken($brand-green, 6%);
    }

    &:disabled {
      cursor: not-allowed;
      background: #cbd5e1;
      opacity: 0.6;
    }
  }

  // "or" rule between the patient picker and the phone box. Sized to the 400px picker so the two
  // controls read as one either/or choice rather than two unrelated fields.
  // ONE width for everything in this card. Each labelled control, the divider, the consent note and
  // the button pair are all 400px capped to 100%, so at any viewport they share one left edge and
  // one right edge. The card used to mix 400px controls with content-sized, independently centred
  // buttons — see the .form-actions comment in the template.
  .field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 400px;
    max-width: 100%;
    min-width: 0;
    // No width override for the control inside: it already carries `width: 400px; max-width: 100%`,
    // which inside a box of exactly that size resolves to the same number at every viewport. An
    // override here would also plant a second rule for the phone box ABOVE its real one, and the
    // iOS focus-zoom guard anchors on the first match of that selector — it went red on exactly
    // that. (This comment deliberately does not spell the selector out, for the same reason.)
  }

  .field-label {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
  }

  .form-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    width: 400px;
    max-width: 100%;

    // Beats the `.chat-forms button { flex-shrink: 0 }` above: equal halves, so the pair reads as
    // one block the width of the fields.
    //
    // The 140px BASIS is what stops "Start Conversation" being clipped. Buttons here are
    // white-space: nowrap, so a half that is too narrow does not wrap the label — it cuts it, and
    // at 390px an even split gave each button 112px for ~110px of text plus 24px of padding, which
    // painted "Start Conversatio". Below 292px of room the two no longer fit side by side, so they
    // wrap and each takes the full field width instead. Nothing is ever clipped, at any viewport.
    button {
      flex: 1 1 140px;
      min-width: 0;
      padding: 10px 12px;
    }
  }

  .or-divider {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 400px;
    max-width: 100%;
    margin: -4px 0;
    color: #94a3b8;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;

    &::before,
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: #e2e8f0;
    }
  }

  // ios-zoom-exempt is NOT claimed here: this box is typed into on phones, and iOS Safari zooms in
  // on focus for anything under 16px and never zooms back out. The app root is 14px, so 1rem would
  // be 14px — the size MUST be written in px.
  .new-number-input {
    width: 400px;
    max-width: 100%;
    height: 42px;
    padding: 0 14px;
    font-size: 16px;
    color: #1e293b;
    background: #fff;
    border: 1px solid #e2e8f0 !important;
    border-radius: 8px;
    outline: none;

    &::placeholder { color: #94a3b8; }

    &:focus {
      border-color: $brand-green !important;
      box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.04);
    }
  }

  .consent-note {
    width: 400px;
    max-width: 100%;
    margin: -4px 0 0 0;
    font-size: 12px;
    line-height: 1.45;
    color: #64748b;

    i { margin-right: 6px; color: #94a3b8; }
  }

  .button-cancel {
    color: #64748b;
    background: #fff;
    border: 1px solid #e2e8f0 !important;
    margin-left: 0;

    &:hover {
      color: #334155;
      background: #f1f5f9;
      border-color: #cbd5e1 !important;
    }
  }
}

// =====================================================
// MULTISELECT STYLING - Separate section for specificity
// =====================================================
.chat-forms :deep(.multiselect) {
  width: 400px;
  // PRE-EXISTING BUG, found 2026-09-18 while checking this form at 390px: `width: 400px` with no
  // cap made the picker 161px wider than the card it sits in, which gave the whole Conversations
  // page a horizontal scrollbar on every phone. `flex-shrink: 0` cannot save it — the row is
  // flex-direction: column at that width, so there is no main-axis shrinking to do.
  max-width: 100%;
  flex-shrink: 0;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;

  .multiselect__tags {
    border: 1px solid #e2e8f0 !important;
    border-radius: 8px;
    padding: 6px 40px 6px 12px;
    min-height: 42px;
    background: #fff !important;
    box-shadow: none !important;
  }

  .multiselect__select {
    background: transparent !important;
    height: 40px;
    width: 36px;
    border: none !important;

    &::before {
      border-color: #94a3b8 transparent transparent transparent;
    }
  }

  .multiselect__spinner {
    background: transparent !important;
    border: none !important;
  }

  .multiselect__tag {
    background-color: $brand-green !important;
    color: #fff;
    border-radius: 6px;
    padding: 5px 26px 5px 10px;
    font-size: 0.8125rem;
    font-weight: 500;
    border: none !important;
  }

  .multiselect__placeholder {
    color: #94a3b8;
    padding-top: 2px;
  }

  .multiselect__option--highlight {
    background: $brand-green !important;
  }

  .multiselect__input {
    background: transparent !important;
    border: none !important;
  }

  .multiselect__content-wrapper {
    background: #fff !important;
    border: 1px solid #e2e8f0 !important;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

// =====================================================
// CHAT WINDOW CONTAINER - Matching Chat page styles
// =====================================================

// Hide the default add room button (we have our own in header)
:deep(.vac-icon-add-room) {
  display: none !important;
}

:deep(.vac-card-window) {
  border-radius: 12px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
  background: #fff !important;
  overflow: hidden !important;
}

// Chat Room List - Subtle sidebar
// Single-pane (phone) mode: vue-advanced-chat keeps `min-width: 260px` even when the list goes full width,
// so on a 320px phone the list overflowed the card. Let the full-width list shrink to the card.
:deep(.vac-rooms-container-full) {
  min-width: 0 !important;
}

:deep(.vac-rooms-container) {
  background: #fafbfc !important;
  border-right: 1px solid #f1f5f9 !important;
  width: 300px;

  .vac-room-item {
    border-radius: 6px !important;
    margin: 2px 6px !important;
    padding: 10px 12px !important;
    transition: background 0.15s ease !important;

    &:hover {
      background: #fff !important;
    }

    &.vac-room-selected {
      background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%) !important;
      border: 1px solid #86efac !important;
      border-left: 3px solid $brand-green !important;
      box-shadow: 0 2px 8px rgba(58, 191, 148, 0.2) !important;
    }
  }

  .vac-room-name {
    font-weight: 500 !important;
    font-size: 0.875rem !important;
    color: #334155 !important;
  }

  .vac-text-last {
    color: #94a3b8 !important;
    font-size: 0.75rem !important;
  }
}

:deep(.vac-chat-container) {
  flex: 1;
  background: #fff;
}

// Room Avatar - Using phone icon for patient conversations
$room-avatar-color: #3b82f6;

:deep(.vac-avatar),
:deep(.vac-room-avatar) {
  width: 44px !important;
  height: 44px !important;
  min-width: 44px !important;
  min-height: 44px !important;
  border-radius: 12px !important;
  background: linear-gradient(135deg, lighten($room-avatar-color, 5%) 0%, darken($room-avatar-color, 8%) 100%) !important;
  margin-right: 14px !important;
  box-shadow: 0 3px 10px rgba(59, 130, 246, 0.25) !important;
  border: none !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
  position: relative !important;
  overflow: visible !important;
  font-size: 0 !important;
  color: transparent !important;

  &::before {
    content: '\f095' !important; // fa-phone icon for patient conversations
    font-family: 'FontAwesome' !important;
    font-size: 1rem !important;
    color: #fff !important;
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
  }
}

// Chat Header
:deep(.vac-room-header) {
  background: #f8fafc !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #e2e8f0 !important;

  .vac-room-name {
    color: #334155 !important;
    font-weight: 600 !important;
    font-size: 0.875rem !important;
  }
}

// Messages Area
:deep(.vac-messages-container) {
  background: #fff !important;
  flex: 1 !important;
  min-height: 0 !important;
}

:deep(.vac-col-messages) {
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  border: none !important;
}

// =====================================================
// MESSAGE STYLING - Moved to unscoped section at bottom
// =====================================================

// Message Input
:deep(.vac-room-footer) {
  background: #f8fafc !important;
  border-top: 1px solid #e2e8f0 !important;
  padding: 12px 16px !important;

  .vac-box-footer {
    background: #fff !important;
    border: 1px solid #e2e8f0 !important;
    border-radius: 10px !important;
    padding: 8px 12px !important;

    &:focus-within {
      border-color: $brand-green !important;
      box-shadow: 0 0 0 2px rgba(58, 191, 148, 0.1) !important;
    }
  }

  textarea {
    font-size: 0.875rem !important;
    color: #334155 !important;
    // Let long messages expand into a comfortably taller box (was capped at 120px) and allow
    // manual drag-resize — the corner handle was showing but did nothing against the 120px cap.
    max-height: 300px !important;
    resize: vertical !important;
    overflow-y: auto !important;

    &::placeholder {
      color: #94a3b8 !important;
    }
  }

  .vac-icon-textarea {
    // Custom action icon (SMS templates) - match library SVG icon color/spacing
    .vac-svg-button .fa-file-text-o {
      font-size: 19px;
      margin: 0 7px;
      color: var(--chat-icon-color-paperclip, #1976d2);
    }
  }

  .vac-send-disabled svg {
    fill: #cbd5e1 !important;
  }

  // Phones: the footer + box padding and roomy icon margins left the textarea ~125px wide at 390px
  // (~55px at 320px). Tighten the chrome so the typing area gets the width.
  @media (max-width: 640px) {
    padding: 8px !important;

    .vac-box-footer {
      padding: 6px 6px 6px 8px !important;
    }

    textarea {
      padding: 8px 10px !important;
      font-size: 16px !important; // < 16px makes iOS Safari zoom the page on focus
    }

    .vac-icon-textarea {
      margin: 6px 0 0 2px !important;

      svg,
      .vac-wrapper {
        margin: 0 4px !important;
      }

      .vac-svg-button .fa-file-text-o {
        margin: 0 4px;
      }
    }
  }
}

// Scrollbar styling
:deep(.vac-rooms-container),
:deep(.vac-messages-container) {
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 2px;

    &:hover {
      background: #cbd5e1;
    }
  }
}

/* Legacy Twilio Conversations media that can no longer be served (mediaSid, no S3 url) */
.legacy-media-expired {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  color: #999;
  font-style: italic;
  font-size: 13px;
  background: #f5f5f5;
  border-radius: 6px;
}

/* Image loading spinner */
.image-loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #1976d2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Image expand icon hover effect */
.image-expand-icon:hover {
  background: rgba(0,0,0,0.8) !important;
}

/* Image Modal/Lightbox Styles */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.image-modal-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: zoomIn 0.2s ease;
}

@keyframes zoomIn {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.image-modal-close {
  position: absolute;
  top: -40px;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: background 0.2s ease;
  z-index: 10001;
}

.image-modal-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.image-modal-image {
  max-width: 100%;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.image-modal-filename {
  margin-top: 16px;
  color: white;
  font-size: 14px;
  text-align: center;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

/* Failed message styles */
.message-slot-failed {
  position: relative;
}

.failed-message-wrapper {
  margin-bottom: 4px;
}

.failed-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffebee;
  color: #c62828;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  border: 1px solid #ef5350;
}

.failed-indicator .fa-exclamation-circle {
  color: #ef5350;
}

.failed-indicator .retry-btn {
  background: #ef5350;
  color: white;
  border: none;
  padding: 2px 8px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: background 0.2s ease;
}

.failed-indicator .retry-btn:hover {
  background: #c62828;
}

/* Pending message styles */
.message-slot-pending {
  opacity: 0.7;
}

.pending-message-wrapper {
  margin-bottom: 4px;
}

.pending-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-style: italic;
}

.pending-indicator .fa-clock-o {
  animation: pulse-opacity 1.5s ease-in-out infinite;
}

@keyframes pulse-opacity {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

// Live SMS guidance bar above the reply composer.
.conv-sms-guidance-bar {
  padding: 6px 12px 0;
}

// Unified SMS/WhatsApp thread — per-message channel badge + "For: [patient]" chip.
.msg-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 4px;
}
.msg-channel-badge,
.msg-for-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 2px 7px;
  border-radius: 10px;
  white-space: nowrap;
}
.msg-channel-badge.is-whatsapp {
  color: #128c7e;
  background: rgba(37, 211, 102, 0.14);
}
.msg-channel-badge.is-sms {
  color: #4b6b8a;
  background: rgba(75, 107, 138, 0.12);
}
.msg-for-chip {
  color: #6a4bab;
  background: rgba(106, 75, 171, 0.12);
}
</style>

<!-- Unscoped styles for vue-advanced-chat component overrides -->
<style lang="scss">
// These styles MUST be unscoped to override vue-advanced-chat internal styles

$brand-green: #3abf94;

// Fix background - ensure consistent white background throughout
.vac-card-window {
  background: #fff !important;

  // Fix any inner borders that may cause issues
  & > div {
    border: none !important;
  }
}

.vac-col-messages {
  background: #fff !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  border: none !important;
}

.vac-messages-container {
  background: #fff !important;
  flex: 1 !important;
  min-height: 0 !important;
}

.vac-container-scroll {
  background: #fff !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
}

// Hide the default add room button
.vac-icon-add-room {
  display: none !important;
}

// =====================================================
// MESSAGE BUBBLE STYLING - CRITICAL OVERRIDES
// Based on actual HTML structure:
// - Incoming: vac-message-card (no vac-message-current)
// - Outgoing: vac-message-card vac-message-current
// =====================================================

// ALL message cards - default to light blue (incoming patient messages)
.vac-message-card {
  background-color: #e0f2fe !important;
  border: 1px solid #bae6fd !important;
  border-radius: 12px !important;
}

// Outgoing messages (from site/you) - green background
// These have the vac-message-current class
.vac-message-card.vac-message-current {
  background-color: #dcfce7 !important;
  border: 1px solid #86efac !important;
}

// =====================================================
// EMOJI REACTION BADGE — bottom-right of bubble
// vue-advanced-chat renders .vac-button-reaction inside
// a classless <span> (no .vac-message-reactions class exists)
// =====================================================
.vac-message-container {
  position: relative !important;
  padding-bottom: 10px !important;
}
// Push timestamp up so badge doesn't obscure it
.vac-message-card {
  padding-bottom: 18px !important;
}
.vac-message-container .vac-button-reaction {
  position: absolute !important;
  bottom: 0 !important;
  top: auto !important;
  right: -16px !important;
  left: auto !important;
  z-index: 2;
  font-size: 12px !important;
  line-height: 16px !important;
  padding: 1px 4px !important;
  border-radius: 10px !important;
  box-shadow: 0 1px 4px rgba(0,0,0,0.18) !important;
}
// Allow badge and emoji picker to overflow the message column
.vac-col-messages {
  overflow: visible !important;
}
// =====================================================
// HIDE OPTIONS BUTTON UNTIL HOVER (the grey circle)
// =====================================================
.vac-message-container .vac-options-container {
  opacity: 0 !important;
  visibility: hidden !important;
  overflow: visible !important;
  transition: opacity 0.15s, visibility 0.15s;
}
.vac-message-container:hover .vac-options-container {
  opacity: 1 !important;
  visibility: visible !important;
}

// =====================================================
// ROOM LIST STYLING - Better visibility for room items
// =====================================================
.vac-rooms-container {
  background: #f8fafc !important;
  padding: 8px 0 !important;

  // Remove any default list styling
  .vac-room-list {
    padding: 0 !important;
  }
}

// Remove default vue-advanced-chat room dividers/borders
.vac-room-divider,
.vac-room-border,
.vac-line-room {
  display: none !important;
  border: none !important;
  height: 0 !important;
}

.vac-room-item {
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  margin: 0 10px 12px 10px !important; // Bottom margin creates gap between items
  padding: 14px 12px !important;
  transition: all 0.15s ease !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;

  &:hover {
    border-color: #cbd5e1 !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
    transform: translateY(-1px);
    // The transform makes the row its own stacking context, so the thread menu (.vac-menu-options) inside it can no
    // longer rise above the NEXT row: every item but the first was covered and unclickable (2026-10-21, "Simulate a
    // patient reply"). Lift the hovered row; the pointer stays inside it while it moves down the menu.
    z-index: 2;
  }

  // Remove any default borders/separators from vue-advanced-chat
  &::before,
  &::after {
    display: none !important;
    border: none !important;
    content: none !important;
  }

  // Remove any internal container borders
  .vac-room-container,
  .vac-name-container,
  .vac-text-ellipsis {
    border: none !important;
    border-bottom: none !important;
  }
}

// Override any border-bottom that creates gray lines
[class*="vac-room"] {
  border-bottom: none !important;
}

// Re-apply our intentional border to room-item only
.vac-room-item {
  border: 1px solid #e2e8f0 !important;
}

.vac-room-item.vac-room-selected {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%) !important;
  border: 1px solid #86efac !important;
  border-left: 3px solid $brand-green !important;
  box-shadow: 0 2px 8px rgba(58, 191, 148, 0.2) !important;
}

.vac-room-name {
  font-weight: 600 !important;
  color: #1e293b !important;
  font-size: 0.9rem !important;
}

.vac-text-last {
  color: #64748b !important;
  font-size: 0.8rem !important;
}

.vac-text-date {
  color: #94a3b8 !important;
  font-size: 0.75rem !important;
}

// Text styling within messages
.vac-text-username {
  color: $brand-green !important;
  font-weight: 500 !important;
  font-size: 0.75rem !important;
}

.vac-text-content {
  color: #334155 !important;
  font-size: 0.875rem !important;
}

.vac-text-timestamp {
  color: #64748b !important;
  font-size: 0.6875rem !important;
}

// =====================================================
// MULTISELECT OVERRIDE STYLES (UNSCOPED)
// vue-multiselect doesn't respond well to :deep() selectors
// =====================================================
.conversations-page .chat-forms {
  // Nuclear option: remove ALL borders from everything inside chat-forms
  *,
  *::before,
  *::after {
    border-top: none !important;
    border-bottom: none !important;
  }

  // Re-apply specific borders we actually want
  border: 1px solid #e2e8f0 !important;

  .multiselect__tags {
    border: 1px solid #e2e8f0 !important;
  }

  .button-cancel {
    border: 1px solid #e2e8f0 !important;
  }
}

// Target multiselect component directly
.conversations-page .multiselect {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.conversations-page .multiselect__tags {
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  background: #fff !important;
  box-shadow: none !important;
}

.conversations-page .multiselect__select {
  background: transparent !important;
  border: none !important;
}

.conversations-page .multiselect__spinner {
  background: transparent !important;
  border: none !important;
}

.conversations-page .multiselect__input {
  background: transparent !important;
  border: none !important;
}

.conversations-page .multiselect__content-wrapper {
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
}

// =====================================================
// RECENTLY USED BAR
// =====================================================
.recent-bar {
  background: #f8fafc;
  padding: 4px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  // Separate Quick Actions from the message box with space, not a hairline flush against it.
  margin-bottom: 8px;
  // Constrain to the footer width so __items can actually overflow & scroll
  min-width: 0;

  // + Insert dropdown sits as the first child of the bar (relocated by renderRecentBar)
  .insert-button-wrapper {
    flex-shrink: 0;
  }

  &__header {
    font-size: 10px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
    cursor: default;

    // Phones: "+ Insert" and this label used the whole row at 320px, leaving the emoji chips 0px wide.
    // The chips explain themselves (and keep their tooltip), so drop the label.
    @media (max-width: 640px) {
      display: none;
    }
  }

  &__items {
    flex: 1 1 0;
    // CRITICAL: min-width:0 on the flex item so its intrinsic content size
    // doesn't prevent shrinking — without this, overflow-x can't kick in and
    // chips clip off the right edge on narrow viewports.
    min-width: 0;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 4px;
    overflow-x: auto;
    overflow-y: hidden;

    &::-webkit-scrollbar {
      height: 0;
    }
  }

  &__divider {
    width: 1px;
    height: 18px;
    background: #cbd5e1;
    flex-shrink: 0;
    margin: 0 2px;
  }
}

// Shared button styles
.recent-btn {
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.15s, transform 0.1s;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1.3;

  &:hover {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  // Emoji buttons
  &--emoji {
    font-size: 16px;
    padding: 2px 4px;

    &:hover {
      background: #e2e8f0;
    }
  }

  // Variable pills
  &--variable {
    font-size: 11px;
    font-weight: 500;
    padding: 2px 8px;
    background: #ede9fe;
    color: #6d28d9;
    border-radius: 10px;

    &:hover {
      background: #ddd6fe;
    }
  }

  // Template pills
  &--template {
    font-size: 11px;
    font-weight: 500;
    padding: 2px 8px;
    background: #e0f2fe;
    color: #0369a1;
    border-radius: 10px;

    .fa {
      font-size: 9px;
      margin-right: 2px;
    }

    &:hover {
      background: #bae6fd;
    }
  }
}
</style>



